import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import OrderCalendar from "../components/OrderCalendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Package,
  Users,
  AlertCircle,
  Plus,
  Edit,
  Lock,
  Unlock,
  Repeat,
  Truck,
  MapPin,
  Clock,
  CheckCircle,
  Search,
  Filter,
  ArrowUpDown,
  X,
  Trash2,
  DollarSign,
  Ban,
  CheckCircle2,
  Key,
  Calendar,
  FileText,
  Mail,
  TrendingUp,
  Tag,
  Eye,
  EyeOff,
  CreditCard, // Add this for credit notes
  ChevronDown, // For sorting dropdown
  Download

} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import CustomerEmailSettings from "../components/CustomerEmailSettings";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function AdminDashboard() {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);

  // Sync URL with tab state
  useEffect(() => {
    if (location.pathname.includes("/invoices")) {
      setActiveTab("invoices");
    }
  }, [location.pathname]);

  // Update URL when tab changes to invoices
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "invoices") {
      navigate("/dashboard/admin/invoices", { replace: true });
    } else if (location.pathname.includes("/invoices")) {
      navigate("/dashboard/admin", { replace: true });
    }
  };

  const [cases, setCases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [frequencyTemplates, setFrequencyTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderForTracking, setSelectedOrderForTracking] =
    useState(null);

  // SKU management
  const [showSkuDialog, setShowSkuDialog] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [skuForm, setSkuForm] = useState({
    name: "",
    category: "",
    price: "",
    unit: "",
    description: "",
  });
  const [creatingSku, setCreatingSku] = useState(false);
  const [deletingSkuId, setDeletingSkuId] = useState(null);

  // Customer Pricing
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPricing, setCustomerPricing] = useState([]);
  const [skusWithPricing, setSkusWithPricing] = useState([]);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    sku_id: "",
    custom_price: "",
  });
  const [creatingPricing, setCreatingPricing] = useState(false);
  const [deletingPricingId, setDeletingPricingId] = useState(null);

  // Loading states for operations
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [updatingCaseId, setUpdatingCaseId] = useState(null);
  const [updatingStatusOrderId, setUpdatingStatusOrderId] = useState(null);
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState(null);
  const [togglingUserId, setTogglingUserId] = useState(null);

  // User edit
  const [showUserEditDialog, setShowUserEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    role: "",
    secondary_email: "",
  });
  const [savingUserEdit, setSavingUserEdit] = useState(false);

  // User creation
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "customer",
    phone: "",
    secondary_email: "",
    address: "",
  });

  // Calculate GST (10%)
  const calculateGST = (amount) => {
    const basePrice = amount;
    const gst = amount * 0.1;
    const total = amount + gst;
    return { basePrice, gst, total };
  };

  const generateInvoice = (order) => {
    const GST_RATE = 0.10;

    /* ---------------- BASIC CALCULATIONS ---------------- */

    const invoiceNumber = `INV-${order.order_number
      .replace("Order-", "")
      .padStart(4, "0")}`;

    const invoiceDate = new Date(order.delivery_date || order.created_at);

    const formatDate = (d) =>
      `${d.getDate()} ${d.toLocaleString("en-AU", { month: "long" })} ${d.getFullYear()}`;

    let subtotalExGST = 0;

    const itemRows = order.items
      .map((item) => {
        const lineExGST = item.price * item.quantity;
        const gst = lineExGST * GST_RATE;
        const lineTotal = lineExGST + gst;

        subtotalExGST += lineExGST;

        return `
        <tr>
          <td>${item.sku_name}</td>
          <td style="text-align:center;">${item.quantity.toFixed(2)}</td>
          <td style="text-align:right;">$${item.price.toFixed(2)}</td>
          <td style="text-align:right;">$${gst.toFixed(2)}</td>
          <td style="text-align:right;">$${lineTotal.toFixed(2)}</td>
        </tr>
      `;
      })
      .join("");

    const deliveryExGST = order.delivery_rate || 0;
    const deliveryGST = deliveryExGST * GST_RATE;
    const deliveryTotal = deliveryExGST + deliveryGST;

    const totalGST = (subtotalExGST + deliveryExGST) * GST_RATE;
    const totalAUD = subtotalExGST + deliveryExGST + totalGST;

    /* ---------------- HTML + PRINT ---------------- */

    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<title>TAX INVOICE</title>

<style>
@page { size: A4; margin: 10mm; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #000; }

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #9ad6c3;
  padding-bottom: 10px;
}

.header-left {
  font-size: 22px;
  font-weight: bold;
}

.header-right {
  text-align: right;
  font-size: 10px;
  line-height: 1.4;
}

.logo { width: 95px; margin-bottom: 4px; }

.company-name {
  font-size: 15px;
  font-weight: bold;
  color: #9ad6c3;
}

.box {
  background: #f4f6f7;
  padding: 8px;
  margin-top: 12px;
}

.flex {
  display: flex;
  justify-content: space-between;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
}

.table th {
  background: #9ad6c3;
  color: #fff;
  padding: 6px;
  font-size: 9px;
}

.table td {
  padding: 6px;
  border-bottom: 1px solid #ddd;
}

.totals {
  width: 40%;
  margin-left: auto;
  margin-top: 10px;
}

.totals div {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.grand {
  font-size: 14px;
  font-weight: bold;
  border-top: 2px solid #9ad6c3;
  margin-top: 6px;
  padding-top: 6px;
  color: #9ad6c3;
}

.payment,
.advice {
  border: 2px solid #9ad6c3;
  padding: 10px;
  margin-top: 15px;
}
</style>
</head>

<body onload="window.print()">

<div class="header">
  <div class="header-left">TAX INVOICE</div>
  <div class="header-right">
    <img src="/assets/logo.png" class="logo" />
    <div class="company-name">INFINITE LAUNDRY SOLUTIONS</div>
    76c Mica St, Carole Park QLD 4300<br />
    Email: info@infinitelaundry.com.au<br />
    ABN: 20 671 606 901
  </div>
</div>

<div class="box flex">
  <div>
    <strong>Invoice Number:</strong><br />
    ${invoiceNumber}<br /><br />
    <strong>Reference:</strong><br />
    Delivery ${order.order_number}
  </div>
  <div style="text-align:right;">
    <strong>Date:</strong><br />
    ${formatDate(invoiceDate)}
  </div>
</div>

<div class="box">
  <strong>Bill To:</strong><br />
  ${order.customer_name}<br />
  ${order.delivery_address}
</div>

<table class="table">
  <thead>
    <tr>
      <th>Description</th>
      <th style="text-align:center;">Qty</th>
      <th style="text-align:right;">Unit Price</th>
      <th style="text-align:right;">GST</th>
      <th style="text-align:right;">Amount AUD</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
    <tr>
      <td><strong>Delivery Charges</strong></td>
      <td style="text-align:center;">1.00</td>
      <td style="text-align:right;">$${deliveryExGST.toFixed(2)}</td>
      <td style="text-align:right;">$${deliveryGST.toFixed(2)}</td>
      <td style="text-align:right;">$${deliveryTotal.toFixed(2)}</td>
    </tr>
  </tbody>
</table>

<div class="totals">
  <div><span>Subtotal</span><span>$${(subtotalExGST + deliveryExGST).toFixed(2)}</span></div>
  <div><span>TOTAL GST 10%</span><span>$${totalGST.toFixed(2)}</span></div>
  <div class="grand"><span>TOTAL AUD</span><span>$${totalAUD.toFixed(2)}</span></div>
</div>

<div class="payment">
  <strong>Payment Information</strong><br /><br />
  Pay by Direct Deposit<br />
  Account Name: Infinite Asset Solutions<br />
  BSB: 034-069<br />
  Account Number: 671254
</div>

<div class="advice">
  <strong style="display:block;text-align:center;">PAYMENT ADVICE</strong><br />
  Customer: ${order.customer_name}<br />
  Invoice Number: ${invoiceNumber}<br />
  Amount Due: $${totalAUD.toFixed(2)}
</div>

</body>
</html>
`;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  // Generate and download invoice as PDF
  const handleDownloadInvoicePDF = async (order) => {
    try {
      const customer = customers.find((c) => c.id === order.customer_id);
      generateInvoice(order, customer);
      toast.success("Invoice PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice PDF:", error);
      toast.error("Failed to download invoice PDF");
    }
  };

  // Password reset
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  // Users Filter & Sort
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSortBy, setUserSortBy] = useState("created_at");
  const [userSortOrder, setUserSortOrder] = useState("desc");
  const [userRoleFilter, setUserRoleFilter] = useState([]);

  // Pagination for users
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userItemsPerPage] = useState(20);

  // SKUs Filter & Sort
  const [skuSearchQuery, setSkuSearchQuery] = useState("");
  const [skuSortBy, setSkuSortBy] = useState("name");
  const [skuSortOrder, setSkuSortOrder] = useState("asc");
  const [skuCategoryFilter, setSkuCategoryFilter] = useState([]);

  // Cases Filter & Sort
  const [caseSearchQuery, setCaseSearchQuery] = useState("");
  const [caseSortBy, setCaseSortBy] = useState("created_at");
  const [caseSortOrder, setCaseSortOrder] = useState("desc");
  const [caseStatusFilter, setCaseStatusFilter] = useState([]);
  const [casePriorityFilter, setCasePriorityFilter] = useState([]);
  const [caseDateFrom, setCaseDateFrom] = useState("");
  const [caseDateTo, setCaseDateTo] = useState("");

  // Orders Filter & Sort
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderSortBy, setOrderSortBy] = useState("created_at");
  const [orderSortOrder, setOrderSortOrder] = useState("desc");
  const [orderStatusFilter, setOrderStatusFilter] = useState([]);
  const [orderRecurringFilter, setOrderRecurringFilter] = useState("all");
  const [orderLockFilter, setOrderLockFilter] = useState("all");
  const [orderDeliveryStatusFilter, setOrderDeliveryStatusFilter] = useState([]);
  const [orderDateFilter, setOrderDateFilter] = useState("all");
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");

  // Pagination for orders
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Delivery Tracking Filters
  const [deliveryDateFrom, setDeliveryDateFrom] = useState("");
  const [deliveryDateTo, setDeliveryDateTo] = useState("");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState([]);

  // Driver assignment
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState({});

  // Customer email settings
  const [showEmailSettingsDialog, setShowEmailSettingsDialog] = useState(false);
  const [selectedCustomerForEmail, setSelectedCustomerForEmail] = useState(null);

  // Pending edit requests
  const [pendingEditRequests, setPendingEditRequests] = useState([]);
  const [showEditRequestDialog, setShowEditRequestDialog] = useState(false);
  const [selectedEditRequest, setSelectedEditRequest] = useState(null);
  const [editRequestAction, setEditRequestAction] = useState("approve");
  const [editRequestForm, setEditRequestForm] = useState({});
  const [rejectionReason, setRejectionReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [processingEditRequest, setProcessingEditRequest] = useState(false);

  // Order form
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const PERMANENT_PICKUP_ADDRESS = "3/76 Mica Street, Carole Park, QLD, 4300";
  const [orderForm, setOrderForm] = useState({
    customer_id: "",
    customer_name: "",
    customer_email: "",
    items: [],
    delivery_date: "",
    pickup_address: PERMANENT_PICKUP_ADDRESS,
    delivery_address: "",
    special_instructions: "",
    is_recurring: false,
    frequency_template_id: "",
  });
  const [orderItems, setOrderItems] = useState([
    { sku_id: "", sku_name: "", quantity: 1, price: 0 },
  ]);
  const [filteredSkus, setFilteredSkus] = useState([]);
  const [customerSkus, setCustomerSkus] = useState([]);

  // Invoice status workflow
  const INVOICE_STATUS = {
    PENDING_REVIEW: 'pending_review',
    APPROVED: 'approved',
    PENDING_PAYMENT: 'pending_payment',
    CLEARED: 'cleared',
    DISPUTE: 'dispute',
  };

  const DEFAULT_STATUS = INVOICE_STATUS.PENDING_REVIEW;

  // INVOICING STATE
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceEmailDialog, setShowInvoiceEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    includePaymentInfo: true,
    emailType: "primary" // 'primary', 'operations', 'finance'
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  // Add these state variables near other state declarations
  const [invoiceSortBy, setInvoiceSortBy] = useState("created_at");
  const [invoiceSortOrder, setInvoiceSortOrder] = useState("desc");
  const [invoiceFilters, setInvoiceFilters] = useState({
    status: [],
    customer: [],
    dateFrom: "",
    dateTo: "",
    amountFrom: "",
    amountTo: "",
    isCreditNote: false // For credit notes filter
  });
  const [showInvoiceFilters, setShowInvoiceFilters] = useState(false);
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState(null);
  const [creditNoteForm, setCreditNoteForm] = useState({
    reason: "",
    amount: 0,
    notes: ""
  });

  // Frequency Templates
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    frequency_type: "daily",
    frequency_value: "1",
    description: "",
  });

  // Addresses
  const [businessPickupAddress, setBusinessPickupAddress] = useState("");
  // Add these state variables near other state declarations (around line 574-592)
  const [showClearedInvoices, setShowClearedInvoices] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [selectedDisputeInvoice, setSelectedDisputeInvoice] = useState(null);
  const [disputeReason, setDisputeReason] = useState("");
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceEditForm, setInvoiceEditForm] = useState({
    customer_name: "",
    customer_email: "",
    items: [],
    subtotal: 0,
    gst_amount: 0,
    total_amount: 0,
    notes: ""
  });
  const [selectedInvoices, setSelectedInvoices] = useState([]); // For bulk actions
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState("");

  // Add these state variables near other state declarations
  const [showEmailHistoryDialog, setShowEmailHistoryDialog] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [autoSendOnApprove, setAutoSendOnApprove] = useState(true);
  const [showAutoSendSettings, setShowAutoSendSettings] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([
    {
      id: 'invoice_approved',
      name: 'Invoice Approved',
      subject: 'Invoice {{invoice_number}} Approved - Infinite Laundry Solutions',
      message: 'Dear {{customer_name}},\n\nYour invoice {{invoice_number}} has been approved and is now pending payment.\n\nTotal Amount: ${{total_amount}}\nDue Date: {{due_date}}\n\nPlease find the attached PDF for payment details.\n\nThank you for your business!',
      default: true
    },
    {
      id: 'payment_reminder',
      name: 'Payment Reminder',
      subject: 'Payment Reminder - Invoice {{invoice_number}}',
      message: 'Dear {{customer_name}},\n\nThis is a friendly reminder that invoice {{invoice_number}} is pending payment.\n\nTotal Amount: ${{total_amount}}\nDue Date: {{due_date}}\n\nPlease arrange payment at your earliest convenience.\n\nThank you for your business!',
      default: true
    },
    {
      id: 'invoice_disputed',
      name: 'Invoice Disputed',
      subject: 'Invoice {{invoice_number}} Dispute - Action Required',
      message: 'Dear {{customer_name}},\n\nYour invoice {{invoice_number}} has been marked as disputed.\n\nReason: {{dispute_reason}}\n\nOur team will review this and get back to you shortly.\n\nThank you for your patience.',
      default: true
    }
  ]);

  // Add this function to handle auto-email on approval
  const handleApproveInvoiceWithEmail = async (invoiceId) => {
    try {
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        toast.error("Invoice not found");
        return;
      }

      if (autoSendOnApprove) {
        // Get finance email
        const financeEmail = getCustomerFinanceEmail(invoice.customer_id);

        // Prepare template data
        const templateData = {
          invoice_number: invoice.invoice_number,
          customer_name: invoice.customer_name,
          total_amount: invoice.total_amount.toFixed(2),
          due_date: new Date(invoice.due_date).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        };

        // Get the invoice approved template
        const template = emailTemplates.find(t => t.id === 'invoice_approved');
        let message = template.message;
        let subject = template.subject;

        // Replace template variables
        Object.keys(templateData).forEach(key => {
          const placeholder = `{{${key}}}`;
          message = message.replace(new RegExp(placeholder, 'g'), templateData[key]);
          subject = subject.replace(new RegExp(placeholder, 'g'), templateData[key]);
        });

        // Show email dialog for customization
        setSelectedInvoice(invoice);
        setEmailForm({
          emailType: 'finance',
          subject: subject,
          message: message,
          includePaymentInfo: true,
          isApprovalEmail: true,
          invoiceId: invoiceId
        });
        setShowInvoiceEmailDialog(true);
      } else {
        // Just approve without email
        await handleUpdateInvoiceStatus(invoiceId, INVOICE_STATUS.APPROVED);
      }
    } catch (error) {
      console.error("Failed to approve invoice:", error);
      toast.error("Failed to approve invoice");
    }
  };

  // Helper function to get default delivery date (tomorrow)
  const getDefaultDeliveryDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().slice(0, 10);
  };

  // Helper function to format date for date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return getDefaultDeliveryDate();

    if (dateString.length === 10 && !dateString.includes('T')) {
      return dateString;
    }

    if (dateString.includes('T')) {
      return dateString.slice(0, 10);
    }

    try {
      const date = new Date(dateString);
      return date.toISOString().slice(0, 10);
    } catch {
      return getDefaultDeliveryDate();
    }
  };

  // Case update
  const [selectedCase, setSelectedCase] = useState(null);
  const [showCaseDialog, setShowCaseDialog] = useState(false);
  const [caseUpdate, setCaseUpdate] = useState({
    status: "",
    resolution: "",
    priority: "",
  });

  // Chart colors
  const CHART_COLORS = {
    primary: "#14b8a6",
    secondary: "#0d9488",
    accent: "#2dd4bf",
    pending: "#f59e0b",
    confirmed: "#3b82f6",
    processing: "#8b5cf6",
    ready: "#10b981",
    "out-for-delivery": "#06b6d4",
    delivered: "#22c55e",
    cancelled: "#ef4444",
  };

  useEffect(() => {
    fetchData();
    fetchAddresses();

    // Also fetch invoices independently if on invoices tab
    if (activeTab === "invoices") {
      fetchInvoices();
    }
  }, [activeTab]);

  // Fetch customer-specific SKUs when customer is selected
  useEffect(() => {
    const fetchCustomerSkus = async () => {
      if (orderForm.customer_id) {
        try {
          const response = await axios.get(
            `${API}/skus-with-pricing/${orderForm.customer_id}`
          );
          setFilteredSkus(response.data);
          setCustomerSkus(response.data);
        } catch (error) {
          console.error("Error fetching customer SKUs:", error);
          setFilteredSkus(skus);
          setCustomerSkus([]);
        }
      } else {
        setFilteredSkus(skus);
        setCustomerSkus([]);
      }
    };

    if (skus.length > 0) {
      fetchCustomerSkus();
    }
  }, [orderForm.customer_id, skus, API]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (activeTab === "overview") {
        const [statsRes, ordersRes, customersRes, casesRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`),
          axios.get(`${API}/orders`),
          axios.get(`${API}/users`),
          axios.get(`${API}/cases`),
        ]);
        setStats(statsRes.data);
        setOrders(ordersRes.data);
        setCustomers(customersRes.data);
        setCases(casesRes.data);
      } else if (activeTab === "orders") {
        const [
          ordersRes,
          customersRes,
          skusRes,
          templatesRes,
          driversRes,
          editRequestsRes,
        ] = await Promise.all([
          axios.get(`${API}/orders`),
          axios.get(`${API}/users`),
          axios.get(`${API}/skus`),
          axios.get(`${API}/frequency-templates`),
          axios.get(`${API}/drivers`),
          axios.get(`${API}/orders/pending-edit-requests`),
        ]);
        setOrders(ordersRes.data);
        setCustomers(customersRes.data);
        setSkus(skusRes.data);
        setFrequencyTemplates(templatesRes.data);
        setDrivers(driversRes.data);
        setPendingEditRequests(editRequestsRes.data);
      } else if (activeTab === "cases") {
        const casesRes = await axios.get(`${API}/cases`);
        setCases(casesRes.data);
      } else if (activeTab === "users") {
        const usersRes = await axios.get(`${API}/users`);
        setCustomers(usersRes.data);
      } else if (activeTab === "delivery-tracking") {
        const ordersRes = await axios.get(`${API}/orders`);
        setOrders(ordersRes.data);
      } else if (activeTab === "calendar") {
        const ordersRes = await axios.get(`${API}/orders`);
        setOrders(ordersRes.data);
      } else if (activeTab === "skus") {
        const skusRes = await axios.get(`${API}/skus`);
        setSkus(skusRes.data);
      } else if (activeTab === "customer-pricing") {
        const [customersRes, skusRes] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/skus`),
        ]);
        setCustomers(customersRes.data);
        setSkus(skusRes.data);
      } else if (activeTab === "frequency-templates") {
        const templatesRes = await axios.get(`${API}/frequency-templates`);
        setFrequencyTemplates(templatesRes.data);
      } else if (activeTab === "invoices") {
        const [invoicesRes, ordersRes, customersRes] = await Promise.all([
          axios.get(`${API}/invoices`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          axios.get(`${API}/orders`),
          axios.get(`${API}/users`),
        ]);

        console.log('Invoices response:', invoicesRes.data);

        // Ensure all invoices have proper status
        const processedInvoices = invoicesRes.data.map(invoice => ({
          ...invoice,
          // Set default status if missing
          status: invoice.status || DEFAULT_STATUS,
          // Ensure invoice_number exists
          invoice_number: invoice.invoice_number || `INV-${invoice.order_number}`
        }));

        setInvoices(processedInvoices);
        setOrders(ordersRes.data);
        setCustomers(customersRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);

      // Specific error handling for invoices
      if (activeTab === "invoices") {
        console.error("Invoice fetch error:", error.response?.data);

        // Check if it's an authentication error
        if (error.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          // Redirect to login
          navigate('/login');
        } else {
          toast.error(error.response?.data?.detail || "Failed to fetch invoices");
        }
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Add this function near your other fetch functions (around line 769-800)
  const fetchInvoices = async () => {
    try {
      console.log('Fetching invoices from:', `${API}/invoices`);
      const token = localStorage.getItem('token');

      const res = await axios.get(`${API}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Invoices response:', res.data);

      // Ensure all invoices have proper status
      const processedInvoices = res.data.map(invoice => ({
        ...invoice,
        // Set default status if missing
        status: invoice.status || DEFAULT_STATUS,
        // Ensure invoice_number exists
        invoice_number: invoice.invoice_number || `INV-${invoice.order_number}`
      }));

      setInvoices(processedInvoices);
    } catch (error) {
      console.error('Failed to fetch invoices', error);
      console.error('Error response:', error.response);

      // Check if it's an authentication error
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        // Redirect to login
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || "Failed to fetch invoices");
      }
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/config/addresses`);
      setBusinessPickupAddress(res.data.business_pickup_address);
    } catch (error) {
      console.error("Failed to fetch addresses", error);
    }
  };

  const fetchCustomerPricing = async (customerId) => {
    try {
      const [skusRes, pricingRes] = await Promise.all([
        axios.get(`${API}/skus-with-pricing/${customerId}`),
        axios.get(`${API}/customer-pricing/${customerId}`),
      ]);
      setSkusWithPricing(skusRes.data);
      setCustomerPricing(pricingRes.data);
    } catch (error) {
      console.error("Failed to fetch customer pricing", error);
    }
  };

  // Helper function to get display name for order status
  const getStatusDisplayName = (status) => {
    const statusMap = {
      pending: "Pending",
      scheduled: "Scheduled",
      processing: "Processing",
      ready_for_pickup: "Order Ready for Pickup",
      out_for_delivery: "Out for Delivery",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };
    return statusMap[status] || status;
  };

  // Chart data preparation functions
  const getRevenueChartData = () => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      last7Days.push({
        date: date.toISOString().split("T")[0],
        revenue: 0,
        orders: 0,
      });
    }

    orders.forEach((order) => {
      if (order.created_at && order.total_price) {
        try {
          const createdDate = new Date(order.created_at);
          const orderDate = createdDate.toISOString().split("T")[0];
          const dayData = last7Days.find((d) => d.date === orderDate);
          if (dayData) {
            dayData.revenue += parseFloat(order.total_price) || 0;
            dayData.orders += 1;
          }
        } catch (e) {
          console.error("Error parsing date for revenue:", order.created_at, e);
        }
      }
    });

    return last7Days.map((day) => ({
      name: new Date(day.date).toLocaleDateString("en-US", {
        weekday: "short",
      }),
      fullDate: new Date(day.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      revenue: parseFloat(day.revenue.toFixed(2)),
      orders: day.orders,
    }));
  };

  const getOrdersByStatusData = () => {
    const statusCounts = {
      pending: 0,
      confirmed: 0,
      processing: 0,
      ready: 0,
      "out-for-delivery": 0,
      delivered: 0,
      cancelled: 0,
    };

    orders.forEach((order) => {
      if (order.status && statusCounts.hasOwnProperty(order.status)) {
        statusCounts[order.status]++;
      }
    });

    const statusLabels = {
      pending: "Pending",
      confirmed: "Confirmed",
      processing: "Processing",
      ready: "Ready",
      "out-for-delivery": "Out for Delivery",
      delivered: "Delivered",
      cancelled: "Cancelled",
    };

    const result = Object.entries(statusCounts)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => ({
        name: statusLabels[status] || status,
        value: count,
        status: status,
      }));

    return result;
  };

  const getTopCustomersData = () => {
    const customerRevenue = {};

    orders.forEach((order) => {
      if (
        order.customer_name &&
        order.total_price &&
        order.status !== "cancelled"
      ) {
        const name = order.customer_name;
        if (!customerRevenue[name]) {
          customerRevenue[name] = 0;
        }
        customerRevenue[name] += parseFloat(order.total_price) || 0;
      }
    });

    return Object.entries(customerRevenue)
      .map(([name, revenue]) => ({
        name: name.length > 15 ? name.substring(0, 15) + "..." : name,
        fullName: name,
        revenue: parseFloat(revenue.toFixed(2)),
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const getCasesData = () => {
    let openCases = 0;
    let inProgressCases = 0;
    let resolvedCases = 0;
    let closedCases = 0;

    cases.forEach((caseItem) => {
      const status = caseItem.status?.toLowerCase();

      if (status === "open") {
        openCases++;
      } else if (status === "in_progress") {
        inProgressCases++;
      } else if (status === "resolved") {
        resolvedCases++;
      } else if (status === "closed") {
        closedCases++;
      }
    });

    const casesData = [
      { name: "Open", value: openCases, color: "#f59e0b", status: "open" },
      {
        name: "In Progress",
        value: inProgressCases,
        color: "#3b82f6",
        status: "in_progress",
      },
      {
        name: "Resolved",
        value: resolvedCases,
        color: "#10b981",
        status: "resolved",
      },
      {
        name: "Closed",
        value: closedCases,
        color: "#6b7280",
        status: "closed",
      },
    ].filter((item) => item.value > 0);

    return casesData;
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (creatingOrder || editingOrderId) return;

    try {
      if (isEditMode && editingOrder) {
        setEditingOrderId(editingOrder.id);
      } else {
        setCreatingOrder(true);
      }

      const customer = customers.find((c) => c.id === orderForm.customer_id);
      const formData = {
        ...orderForm,
        customer_name: customer.full_name,
        customer_email: customer.email,
        items: orderItems.map((item) => {
          const sku = customerSkus.length > 0 ? customerSkus.find((s) => s.id === item.sku_id) : filteredSkus.find((s) => s.id === item.sku_id);
          const price = sku?.customer_price !== undefined ? sku.customer_price : sku?.price || 0;
          return {
            sku_id: item.sku_id,
            sku_name: sku?.name || item.sku_name,
            quantity: parseInt(item.quantity),
            price: price,
          };
        }),
      };

      // Include recurring data only if is_recurring is true
      if (formData.is_recurring && formData.frequency_template_id) {
        const template = frequencyTemplates.find(
          (t) => t.id === formData.frequency_template_id
        );
        formData.recurrence_pattern = {
          frequency_type: template.frequency_type,
          frequency_value: template.frequency_value,
        };
      }

      if (isEditMode && editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, formData);
      } else {
        await axios.post(`${API}/orders`, formData);
      }

      setShowOrderDialog(false);
      setIsEditMode(false);
      setEditingOrder(null);
      setOrderForm({
        customer_id: "",
        customer_name: "",
        customer_email: "",
        items: [],
        delivery_date: "",
        pickup_address: PERMANENT_PICKUP_ADDRESS,
        delivery_address: "",
        special_instructions: "",
        is_recurring: false,
        frequency_template_id: "",
      });
      setOrderItems([{ sku_id: "", sku_name: "", quantity: 1, price: 0 }]);
      fetchData();
      toast.success(`Order ${isEditMode ? "updated" : "created"} successfully`);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        `Failed to ${isEditMode ? "update" : "create"} order`
      );
    } finally {
      setCreatingOrder(false);
      setEditingOrderId(null);
    }
  };

  const handleEditOrder = (order) => {
    setIsEditMode(true);
    setEditingOrder(order);

    let templateId = order.frequency_template_id || "";
    if (order.is_recurring && order.recurrence_pattern && !templateId) {
      const matchingTemplate = frequencyTemplates.find(
        (t) =>
          t.frequency_type === order.recurrence_pattern.frequency_type &&
          t.frequency_value === order.recurrence_pattern.frequency_value
      );
      if (matchingTemplate) {
        templateId = matchingTemplate.id;
      }
    }

    setOrderForm({
      customer_id: order.customer_id,
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      delivery_date: formatDateForInput(order.delivery_date),
      pickup_address: order.pickup_address,
      delivery_address: order.delivery_address,
      special_instructions: order.special_instructions || "",
      is_recurring: order.is_recurring || false,
      frequency_template_id: templateId,
    });
    setOrderItems(
      order.items.map((item) => ({
        sku_id: item.sku_id,
        sku_name: item.sku_name,
        quantity: item.quantity,
        price: item.price,
      }))
    );
    setShowOrderDialog(true);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    if (deletingOrderId) return;

    try {
      setDeletingOrderId(orderId);
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete order");
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    if (updatingStatusOrderId) return;

    try {
      setUpdatingStatusOrderId(orderId);
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    } finally {
      setUpdatingStatusOrderId(null);
    }
  };

  const handleAssignDriver = async (orderId) => {
    const driverId = selectedDriver[orderId];
    if (!driverId) {
      toast.error("Please select a driver");
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}/assign-driver`, {
        driver_id: driverId,
      });
      toast.success("Driver assigned successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign driver");
    }
  };

  const handleUnassignDriver = async (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to unassign the driver from this order?"
      )
    ) {
      return;
    }
    try {
      await axios.put(`${API}/orders/${orderId}/unassign-driver`);
      toast.success("Driver unassigned successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign driver");
    }
  };

  const handleViewEditRequest = (order) => {
    setSelectedEditRequest(order);
    setEditRequestForm({
      delivery_date:
        order.pending_modifications?.delivery_date || order.delivery_date,
      delivery_address:
        order.pending_modifications?.delivery_address || order.delivery_address,
      special_instructions:
        order.pending_modifications?.special_instructions ||
        order.special_instructions,
      items: order.pending_modifications?.items || order.items,
      recurrence_pattern:
        order.pending_modifications?.recurrence_pattern ||
        order.recurrence_pattern,
    });
    setEditRequestAction("approve");
    setRejectionReason("");
    setAdminNotes("");
    setShowEditRequestDialog(true);
  };

  const handleProcessEditRequest = async () => {
    if (processingEditRequest) return;

    if (editRequestAction === "reject" && !rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setProcessingEditRequest(true);
    try {
      const payload = {
        action: editRequestAction,
        rejection_reason:
          editRequestAction === "reject" ? rejectionReason : undefined,
        admin_notes: adminNotes || undefined,
      };

      // Include modified data if action is 'modify'
      if (editRequestAction === "modify") {
        payload.delivery_date = editRequestForm.delivery_date;
        payload.delivery_address = editRequestForm.delivery_address;
        payload.special_instructions = editRequestForm.special_instructions;
        payload.items = editRequestForm.items;
        payload.recurrence_pattern = editRequestForm.recurrence_pattern;
      }

      await axios.put(
        `${API}/orders/${selectedEditRequest.id}/review-edit-request`,
        payload
      );

      const actionText =
        editRequestAction === "reject"
          ? "rejected"
          : editRequestAction === "modify"
            ? "modified and approved"
            : "approved";
      toast.success(`Edit request ${actionText} successfully!`);

      setShowEditRequestDialog(false);
      setSelectedEditRequest(null);
      fetchData();
    } catch (error) {
      console.error("Failed to process edit request:", error);
      toast.error(
        error.response?.data?.detail || "Failed to process edit request"
      );
    } finally {
      setProcessingEditRequest(false);
    }
  };

  const handleUpdateCase = async (e) => {
    e.preventDefault();
    if (updatingCaseId) return;

    try {
      setUpdatingCaseId(selectedCase.id);
      await axios.put(`${API}/cases/${selectedCase.id}`, caseUpdate);
      setShowCaseDialog(false);
      setSelectedCase(null);
      setCaseUpdate({ status: "", resolution: "", priority: "" });
      fetchData();
      toast.success("Case updated successfully");
    } catch (error) {
      toast.error("Failed to update case");
    } finally {
      setUpdatingCaseId(null);
    }
  };

  // User Management Functions
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, userForm);
      setShowUserDialog(false);
      setUserForm({
        email: "",
        password: "",
        full_name: "",
        role: "customer",
        phone: "",
        address: "",
      });
      fetchData();
      toast.success("User created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleToggleUserStatus = async (user) => {
    const action = user.is_active ? "disable" : "enable";
    if (
      !window.confirm(
        `Are you sure you want to ${action} ${user.full_name}'s account?`
      )
    )
      return;

    setTogglingUserId(user.id);
    try {
      await axios.put(`${API}/admin/users/${user.id}/toggle-status`);
      toast.success(`User account ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to reset the password for ${selectedUser?.full_name}?`
      )
    ) {
      return;
    }

    setResettingPasswordUserId(selectedUser.id);
    try {
      await axios.put(`${API}/admin/reset-password/${selectedUser.id}`, {
        new_password: newPassword,
      });
      toast.success("Password reset successfully");
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (error) {
      console.error("Failed to reset password:", error);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const openPasswordResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPasswordDialog(true);
  };

  const openUserEditDialog = (u) => {
    setEditingUser(u);
    setUserEditForm({
      full_name: u.full_name || "",
      email: u.email || "",
      phone: u.phone || "",
      address: u.address || "",
      role: u.role || "customer",
      secondary_email: u.secondary_email || "",
    });
    setShowUserEditDialog(true);
  };

  const handleSaveUserEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      setSavingUserEdit(true);
      const payload = {};
      if (userEditForm.full_name !== editingUser.full_name)
        payload.full_name = userEditForm.full_name;
      if (userEditForm.email !== editingUser.email)
        payload.email = userEditForm.email;
      if ((userEditForm.phone || "") !== (editingUser.phone || ""))
        payload.phone = userEditForm.phone;
      if ((userEditForm.address || "") !== (editingUser.address || ""))
        payload.address = userEditForm.address;
      if (userEditForm.role !== editingUser.role)
        payload.role = userEditForm.role;
      if ((userEditForm.secondary_email || "") !== (editingUser.secondary_email || ""))
        payload.secondary_email = userEditForm.secondary_email;

      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success("User updated successfully");
      setShowUserEditDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update user", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSavingUserEdit(false);
    }
  };

  const handleLockOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/lock`);
      toast.success("Order locked successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to lock order:", error);
      toast.error(error.response?.data?.detail || "Failed to lock order");
    }
  };

  const handleUnlockOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/unlock`);
      toast.success("Order unlocked successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to unlock order:", error);
      toast.error(error.response?.data?.detail || "Failed to unlock order");
    }
  };

  const addOrderItem = () => {
    setOrderItems([
      ...orderItems,
      { sku_id: "", sku_name: "", quantity: 1, price: 0 },
    ]);
  };

  const removeOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const updateOrderItem = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = value;
    setOrderItems(newItems);
  };

  // SKU Management Functions
  const handleCreateSku = async (e) => {
    e.preventDefault();
    if (creatingSku) return;

    try {
      setCreatingSku(true);
      if (editingSku) {
        await axios.put(`${API}/skus/${editingSku.id}`, skuForm);
        toast.success("SKU updated successfully");
      } else {
        await axios.post(`${API}/skus`, skuForm);
        toast.success("SKU created successfully");
      }
      setShowSkuDialog(false);
      setEditingSku(null);
      setSkuForm({
        name: "",
        category: "",
        price: "",
        unit: "",
        description: "",
      });
      fetchData();
    } catch (error) {
      console.error("Failed to save SKU", error);
      toast.error(
        error.response?.data?.detail ||
        `Failed to ${editingSku ? "update" : "create"} SKU`
      );
    } finally {
      setCreatingSku(false);
    }
  };

  const handleEditSku = (sku) => {
    setEditingSku(sku);
    setSkuForm({
      name: sku.name,
      category: sku.category,
      price: sku.price.toString(),
      unit: sku.unit,
      description: sku.description || "",
    });
    setShowSkuDialog(true);
  };

  const handleDeleteSku = async (skuId) => {
    if (!window.confirm("Are you sure you want to delete this SKU?")) return;
    if (deletingSkuId) return;

    try {
      setDeletingSkuId(skuId);
      await axios.delete(`${API}/skus/${skuId}`);
      toast.success("SKU deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to delete SKU", error);
      toast.error(error.response?.data?.detail || "Failed to delete SKU");
    } finally {
      setDeletingSkuId(null);
    }
  };

  // Customer Pricing Functions
  const handleSelectCustomer = async (customerId) => {
    if (!customerId) {
      setSelectedCustomer(null);
      setCustomerPricing([]);
      setSkusWithPricing([]);
      return;
    }

    try {
      const customer = customers.find((c) => c.id === customerId);
      setSelectedCustomer(customer);

      const pricingRes = await axios.get(
        `${API}/customer-pricing/${customerId}`
      );
      setCustomerPricing(pricingRes.data);

      const skusWithCustomPrices = skus.map((sku) => {
        const customPrice = pricingRes.data.find((p) => p.sku_id === sku.id);
        return {
          ...sku,
          custom_price: customPrice ? customPrice.custom_price : null,
          pricing_id: customPrice ? customPrice.id : null,
        };
      });
      setSkusWithPricing(skusWithCustomPrices);
    } catch (error) {
      console.error("Failed to fetch customer pricing", error);
      toast.error("Failed to load customer pricing");
    }
  };

  const handleCreatePricing = async (e) => {
    e.preventDefault();
    if (creatingPricing || !selectedCustomer) return;

    try {
      setCreatingPricing(true);
      await axios.post(`${API}/customer-pricing`, {
        customer_id: selectedCustomer.id,
        sku_id: pricingForm.sku_id,
        custom_price: parseFloat(pricingForm.custom_price),
      });
      toast.success("Custom pricing added successfully");
      setShowPricingDialog(false);
      setPricingForm({ sku_id: "", custom_price: "" });
      handleSelectCustomer(selectedCustomer.id);
    } catch (error) {
      console.error("Failed to create pricing", error);
      toast.error(
        error.response?.data?.detail || "Failed to add custom pricing"
      );
    } finally {
      setCreatingPricing(false);
    }
  };

  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm("Are you sure you want to remove this custom pricing?"))
      return;
    if (deletingPricingId || !selectedCustomer) return;

    try {
      setDeletingPricingId(pricingId);
      await axios.delete(`${API}/customer-pricing/${pricingId}`);
      toast.success("Custom pricing removed successfully");
      handleSelectCustomer(selectedCustomer.id);
    } catch (error) {
      console.error("Failed to delete pricing", error);
      toast.error(
        error.response?.data?.detail || "Failed to remove custom pricing"
      );
    } finally {
      setDeletingPricingId(null);
    }
  };

  // Frequency Template Functions
  const handleCreateOrUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      const templateData = {
        ...templateForm,
        frequency_value: parseInt(templateForm.frequency_value),
      };
      if (editingTemplate) {
        await axios.put(
          `${API}/frequency-templates/${editingTemplate.id}`,
          templateData
        );
      } else {
        await axios.post(`${API}/frequency-templates`, templateData);
      }
      setShowTemplateDialog(false);
      setTemplateForm({
        name: "",
        frequency_type: "daily",
        frequency_value: "1",
        description: "",
      });
      setEditingTemplate(null);
      fetchData();
      toast.success(`Template ${editingTemplate ? "updated" : "created"} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?"))
      return;
    try {
      await axios.delete(`${API}/frequency-templates/${templateId}`);
      toast.success("Template deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      frequency_type: template.frequency_type,
      frequency_value: template.frequency_value.toString(),
      description: template.description || "",
    });
    setShowTemplateDialog(true);
  };

  // Filter and Sort Functions
  const getFilteredAndSortedUsers = () => {
    let filtered = [...customers];

    // Search filter
    if (userSearchQuery) {
      filtered = filtered.filter(
        (user) =>
          user.full_name
            .toLowerCase()
            .includes(userSearchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
      );
    }

    // Role filter
    if (userRoleFilter.length > 0) {
      filtered = filtered.filter((user) => userRoleFilter.includes(user.role));
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (userSortBy) {
        case "name":
          aVal = a.full_name.toLowerCase();
          bVal = b.full_name.toLowerCase();
          break;
        case "email":
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case "role":
          aVal = a.role;
          bVal = b.role;
          break;
        case "created_at":
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return userSortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return userSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const getFilteredAndSortedSkus = () => {
    let filtered = [...skus];

    // Search filter
    if (skuSearchQuery) {
      filtered = filtered.filter(
        (sku) =>
          sku.name.toLowerCase().includes(skuSearchQuery.toLowerCase()) ||
          (sku.description &&
            sku.description
              .toLowerCase()
              .includes(skuSearchQuery.toLowerCase()))
      );
    }

    // Category filter
    if (skuCategoryFilter.length > 0) {
      filtered = filtered.filter((sku) =>
        skuCategoryFilter.includes(sku.category)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (skuSortBy) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "category":
          aVal = a.category.toLowerCase();
          bVal = b.category.toLowerCase();
          break;
        case "price":
          aVal = a.price;
          bVal = b.price;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return skuSortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return skuSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const clearUserFilters = () => {
    setUserSearchQuery("");
    setUserSortBy("created_at");
    setUserSortOrder("desc");
    setUserRoleFilter([]);
    setUserCurrentPage(1);
  };

  const clearSkuFilters = () => {
    setSkuSearchQuery("");
    setSkuSortBy("name");
    setSkuSortOrder("asc");
    setSkuCategoryFilter([]);
  };

  const toggleUserRoleFilter = (role) => {
    setUserRoleFilter((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
    setUserCurrentPage(1);
  };

  const toggleSkuCategoryFilter = (category) => {
    setSkuCategoryFilter((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const getUniqueCategories = () => {
    return [...new Set(skus.map((sku) => sku.category))].sort();
  };

  // Case Filter & Sort Functions
  const getFilteredAndSortedCases = () => {
    let filtered = [...cases];

    // Search filter
    if (caseSearchQuery) {
      const query = caseSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.case_number?.toLowerCase().includes(query) ||
          c.customer_name?.toLowerCase().includes(query) ||
          c.customer_email?.toLowerCase().includes(query) ||
          c.subject?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (caseStatusFilter.length > 0) {
      filtered = filtered.filter((c) => caseStatusFilter.includes(c.status));
    }

    // Priority filter
    if (casePriorityFilter.length > 0) {
      filtered = filtered.filter((c) =>
        casePriorityFilter.includes(c.priority)
      );
    }

    // Date range filter
    if (caseDateFrom || caseDateTo) {
      filtered = filtered.filter((c) => {
        const caseDate = new Date(c.created_at);
        const fromDate = caseDateFrom ? new Date(caseDateFrom) : null;
        const toDate = caseDateTo
          ? new Date(caseDateTo + "T23:59:59.999")
          : null;

        if (fromDate && caseDate < fromDate) return false;
        if (toDate && caseDate > toDate) return false;
        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (caseSortBy) {
        case "case_number":
          aVal = a.case_number || "";
          bVal = b.case_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "priority":
          const priorityOrder = { low: 1, medium: 2, high: 3, urgent: 4 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
      }

      if (aVal < bVal) return caseSortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return caseSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const toggleCaseStatusFilter = (status) => {
    setCaseStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const toggleCasePriorityFilter = (priority) => {
    setCasePriorityFilter((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  };

  const clearCaseFilters = () => {
    setCaseSearchQuery("");
    setCaseSortBy("created_at");
    setCaseSortOrder("desc");
    setCaseStatusFilter([]);
    setCasePriorityFilter([]);
    setCaseDateFrom("");
    setCaseDateTo("");
  };

  // Order Filter & Sort Functions
  const getFilteredAndSortedOrders = () => {
    let filtered = [...orders];

    // Status filter logic
    if (orderStatusFilter.length > 0) {
      filtered = filtered.filter((o) => orderStatusFilter.includes(o.status));
    } else {
      filtered = filtered.filter(
        (o) => o.status !== "delivered" && o.status !== "cancelled"
      );
    }

    // Search filter
    if (orderSearchQuery.trim()) {
      const query = orderSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(query) ||
          order.customer_name?.toLowerCase().includes(query) ||
          order.customer_email?.toLowerCase().includes(query)
      );
    }

    // Delivery Status filter
    if (orderDeliveryStatusFilter.length > 0) {
      filtered = filtered.filter(
        (order) =>
          order.delivery_status &&
          orderDeliveryStatusFilter.includes(order.delivery_status)
      );
    }

    // Type filter
    if (orderTypeFilter !== "all") {
      filtered = filtered.filter((order) =>
        orderTypeFilter === "recurring"
          ? order.is_recurring
          : !order.is_recurring
      );
    }

    // Recurring filter (backward compatibility)
    if (orderRecurringFilter === "recurring") {
      filtered = filtered.filter((o) => o.is_recurring === true);
    } else if (orderRecurringFilter === "one-time") {
      filtered = filtered.filter((o) => !o.is_recurring);
    }

    // Lock filter
    if (orderLockFilter === "locked") {
      filtered = filtered.filter((o) => o.is_locked === true);
    } else if (orderLockFilter === "unlocked") {
      filtered = filtered.filter((o) => !o.is_locked);
    }

    // Date filter
    if (orderDateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((order) => {
        const deliveryDate = new Date(order.delivery_date);

        switch (orderDateFilter) {
          case "today":
            return deliveryDate.toDateString() === today.toDateString();
          case "week":
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return deliveryDate >= weekAgo;
          case "month":
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return deliveryDate >= monthAgo;
          case "custom":
            if (orderDateFrom && orderDateTo) {
              const fromDate = new Date(orderDateFrom);
              const toDate = new Date(orderDateTo);
              toDate.setHours(23, 59, 59, 999);
              return deliveryDate >= fromDate && deliveryDate <= toDate;
            } else if (orderDateFrom) {
              const fromDate = new Date(orderDateFrom);
              return deliveryDate >= fromDate;
            } else if (orderDateTo) {
              const toDate = new Date(orderDateTo);
              toDate.setHours(23, 59, 59, 999);
              return deliveryDate <= toDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (orderSortBy) {
        case "order_number":
          aVal = a.order_number || "";
          bVal = b.order_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "total_amount":
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case "delivery_date":
          aVal = new Date(a.delivery_date || 0);
          bVal = new Date(b.delivery_date || 0);
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
      }

      if (
        orderSortBy === "total_amount" ||
        orderSortBy === "delivery_date" ||
        orderSortBy === "created_at"
      ) {
        return orderSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const aStr = (aVal ?? "").toString().toLowerCase().trim();
        const bStr = (bVal ?? "").toString().toLowerCase().trim();
        const comparison = aStr.localeCompare(bStr, undefined, {
          sensitivity: "base",
        });
        return orderSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  const toggleOrderStatusFilter = (status) => {
    setOrderStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const toggleDeliveryStatusFilter = (status) => {
    setOrderDeliveryStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
    setCurrentPage(1);
  };

  const clearOrderFilters = () => {
    setOrderSearchQuery("");
    setOrderStatusFilter([]);
    setOrderDeliveryStatusFilter([]);
    setOrderDateFilter("all");
    setOrderDateFrom("");
    setOrderDateTo("");
    setOrderTypeFilter("all");
    setOrderRecurringFilter("all");
    setOrderLockFilter("all");
    setOrderSortBy("created_at");
    setOrderSortOrder("desc");
    setCurrentPage(1);
  };

  // Pagination helpers
  const getPaginatedOrders = () => {
    const filtered = getFilteredAndSortedOrders();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredAndSortedOrders();
    return Math.ceil(filtered.length / itemsPerPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // User Pagination helpers
  const getPaginatedUsers = () => {
    const filtered = getFilteredAndSortedUsers();
    const startIndex = (userCurrentPage - 1) * userItemsPerPage;
    const endIndex = startIndex + userItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getUserTotalPages = () => {
    const filtered = getFilteredAndSortedUsers();
    return Math.ceil(filtered.length / userItemsPerPage);
  };

  const handleUserPageChange = (newPage) => {
    setUserCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delivery tracking filter functions
  const getFilteredDeliveryOrders = () => {
    return orders
      .filter((o) => o.driver_id)
      .filter((order) => {
        // Date filtering
        if (
          deliveryDateFrom &&
          new Date(order.delivery_date) < new Date(deliveryDateFrom)
        ) {
          return false;
        }
        if (
          deliveryDateTo &&
          new Date(order.delivery_date) > new Date(deliveryDateTo + " 23:59:59")
        ) {
          return false;
        }

        // Status filtering
        if (deliveryStatusFilter.length > 0) {
          const currentStatus = order.delivery_status || "assigned";
          if (!deliveryStatusFilter.includes(currentStatus)) {
            return false;
          }
        }

        return true;
      });
  };

  // Add this function near other filter functions (around line 2117-2127)
  const getFilteredInvoices = () => {
    let filtered = [...invoices];

    // Filter out cleared invoices unless explicitly showing them
    if (!showClearedInvoices) {
      filtered = filtered.filter(invoice => invoice.status !== INVOICE_STATUS.CLEARED);
    }

    return filtered;
  };

  const toggleDeliveryTrackingStatusFilter = (status) => {
    setDeliveryStatusFilter((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const clearDeliveryFilters = () => {
    setDeliveryDateFrom("");
    setDeliveryDateTo("");
    setDeliveryStatusFilter([]);
  };

  const generateAndSaveInvoice = async (order) => {
    try {
      const itemsTotal = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const deliveryCharge = 120.00; // Fixed delivery charge like in sample
      const subtotal = itemsTotal + deliveryCharge;
      const gstAmount = subtotal * 0.1;
      const totalAmount = subtotal + gstAmount;

      const invoiceData = {
        order_id: order.id,
        order_number: order.order_number,
        customer_id: order.customer_id,
        customer_name: order.customer_name,
        customer_email: order.customer_email,
        invoice_date: new Date().toISOString().split('T')[0],
        delivery_date: order.delivery_date,
        items: order.items,
        subtotal: itemsTotal,
        delivery_rate: deliveryCharge, // Add delivery charge
        gst_amount: gstAmount,
        total_amount: totalAmount,
        status: "pending_approval",
        is_paid: false
      };

      await axios.post(`${API}/invoices`, invoiceData);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to save invoice", error);
      toast.error("Failed to save invoice");
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!selectedInvoice) return;

    setSendingEmail(true);
    try {
      let toEmail;
      switch (emailForm.emailType) {
        case 'operations':
          toEmail = selectedInvoice.operations_email || selectedInvoice.customer_email;
          break;
        case 'finance':
          toEmail = getCustomerFinanceEmail(selectedInvoice.customer_id) || selectedInvoice.customer_email;
          break;
        default:
          toEmail = selectedInvoice.customer_email;
      }

      const emailData = {
        to_email: toEmail,
        subject: emailForm.subject,
        message: emailForm.message,
        include_payment_info: emailForm.includePaymentInfo,
        email_type: emailForm.emailType
      };

      await axios.post(`${API}/invoices/${selectedInvoice.id}/send-email`, emailData);

      // If this is an approval email, update status to pending payment
      if (emailForm.isApprovalEmail && emailForm.invoiceId) {
        await axios.put(`${API}/invoices/${emailForm.invoiceId}`, {
          status: INVOICE_STATUS.PENDING_PAYMENT,
          sent_at: new Date().toISOString()
        });

        toast.success(`Invoice approved and email sent to ${toEmail}`);
      } else {
        toast.success(`Email sent to ${toEmail}`);
      }

      // Close dialog and refresh
      setShowInvoiceEmailDialog(false);
      setSelectedInvoice(null);
      setEmailForm({
        subject: "",
        message: "",
        includePaymentInfo: true,
        emailType: "finance"
      });

      fetchInvoices();

    } catch (error) {
      console.error("Failed to send invoice email", error);
      toast.error(error.response?.data?.detail || "Failed to send invoice email");
    } finally {
      setSendingEmail(false);
    }
  };

  const loadEmailHistory = async (invoiceId) => {
    try {
      setLoadingEmailHistory(true);
      const response = await axios.get(`${API}/invoices/${invoiceId}/email-history`);
      setEmailHistory(response.data);
      setSelectedInvoiceForHistory(invoices.find(inv => inv.id === invoiceId));
      setShowEmailHistoryDialog(true);
    } catch (error) {
      console.error("Failed to load email history", error);
      toast.error("Failed to load email history");
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  // Updated function for automatic email on approval (around line 1160-1232)
  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      const currentInvoice = invoices.find(inv => inv.id === invoiceId);

      if (!currentInvoice) {
        toast.error("Invoice not found");
        return;
      }

      // If approving and auto-send is enabled, use the approval flow
      if (newStatus === INVOICE_STATUS.APPROVED && autoSendOnApprove) {
        await handleApproveInvoiceWithEmail(invoiceId);
        return;
      }

      // For other status changes
      const updateData = { status: newStatus };

      switch (newStatus) {
        case INVOICE_STATUS.APPROVED:
          updateData.approved_at = new Date().toISOString();
          updateData.approved_by = user?.id || 'admin';
          toast.success("Invoice approved");
          break;

        case INVOICE_STATUS.PENDING_PAYMENT:
          toast.info("Invoice marked as pending payment");
          break;

        case INVOICE_STATUS.CLEARED:
          updateData.is_paid = true;
          updateData.paid_date = new Date().toISOString();
          updateData.paid_amount = currentInvoice.total_amount;
          toast.success("Invoice marked as cleared and paid");
          break;

        case INVOICE_STATUS.DISPUTE:
          setSelectedDisputeInvoice(currentInvoice);
          setDisputeReason("");
          setShowDisputeDialog(true);
          return;

        default:
          break;
      }

      updateData.updated_at = new Date().toISOString();
      await axios.put(`${API}/invoices/${invoiceId}`, updateData);
      fetchInvoices();

    } catch (error) {
      console.error("Failed to update invoice status", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice status");
    }
  };

  // Added dispute submission handler 2284-2315
  const handleSubmitDispute = async () => {
    if (!selectedDisputeInvoice || !disputeReason.trim()) {
      toast.error("Please enter a dispute reason");
      return;
    }

    try {
      await axios.put(`${API}/invoices/${selectedDisputeInvoice.id}`, {
        status: INVOICE_STATUS.DISPUTE,
        dispute_reason: disputeReason,
        disputed_at: new Date().toISOString(),
        disputed_by: user?.id || 'admin'
      });

      toast.success("Invoice marked as disputed");
      setShowDisputeDialog(false);
      setSelectedDisputeInvoice(null);
      setDisputeReason("");
      fetchData();

    } catch (error) {
      console.error("Failed to mark invoice as disputed", error);
      toast.error(error.response?.data?.detail || "Failed to mark as disputed");
    }
  };


  const handleDeleteInvoice = async (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (!invoice.is_paid) {
      toast.error("Only paid invoices can be deleted");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this paid invoice? This action cannot be undone.")) return;

    setDeletingInvoice(invoiceId);
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to delete invoice", error);
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    } finally {
      setDeletingInvoice(null);
    }
  };

  const handleApproveInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, {
        status: "approved",
        approved_at: new Date().toISOString(),
        approved_by: "admin"
      });

      toast.success("Invoice approved");
      fetchData();
    } catch (error) {
      console.error("Failed to approve invoice", error);
      toast.error(error.response?.data?.detail || "Failed to approve invoice");
    }
  };

  // Added this function in OwnerDashboard.js 2367-2398
  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceEditForm({
      customer_name: invoice.customer_name,
      customer_email: invoice.customer_email,
      items: invoice.items || [],
      subtotal: invoice.subtotal || 0,
      gst_amount: invoice.gst_amount || 0,
      total_amount: invoice.total_amount || 0,
      notes: invoice.notes || ""
    });
    setShowEditInvoiceDialog(true);
  };

  const handleSaveInvoiceEdit = async () => {
    if (!editingInvoice) return;

    try {
      // Update invoice in backend
      await axios.put(`${API}/invoices/${editingInvoice.id}`, {
        ...invoiceEditForm,
        status: editingInvoice.status === INVOICE_STATUS.DISPUTE ?
          INVOICE_STATUS.DISPUTE : // Stay in dispute if editing disputed invoice
          INVOICE_STATUS.PENDING_REVIEW, // Return to pending review for other statuses
        updated_at: new Date().toISOString(),
        updated_by: user?.id || 'owner'
      });

      // Update PDF (backend should regenerate)
      await axios.post(`${API}/invoices/${editingInvoice.id}/regenerate-pdf`);

      toast.success("Invoice updated successfully. PDF regenerated.");
      setShowEditInvoiceDialog(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error("Failed to update invoice", error);
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    }
  };

  // Bulk actions handler
  const handleBulkAction = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Please select invoices first");
      return;
    }

    try {
      switch (bulkAction) {
        case 'send_email':
          // Send emails to all selected invoices
          await Promise.all(
            selectedInvoices.map(invoiceId =>
              axios.post(`${API}/invoices/${invoiceId}/send-email`, {
                subject: "Invoice Reminder",
                message: "Please find your invoice attached."
              })
            )
          );
          toast.success(`Emails sent to ${selectedInvoices.length} invoice(s)`);
          break;

        case 'mark_paid':
          // Mark all selected as paid/cleared
          await Promise.all(
            selectedInvoices.map(invoiceId =>
              axios.put(`${API}/invoices/${invoiceId}`, {
                status: INVOICE_STATUS.CLEARED,
                is_paid: true,
                paid_date: new Date().toISOString()
              })
            )
          );
          toast.success(`${selectedInvoices.length} invoice(s) marked as paid`);
          break;

        case 'download_pdf':
          // Download PDFs for all selected
          selectedInvoices.forEach(invoiceId => {
            const invoice = invoices.find(i => i.id === invoiceId);
            if (invoice) {
              handleDownloadInvoicePDF(invoice);
            }
          });
          toast.success(`${selectedInvoices.length} PDF(s) downloading`);
          break;

        default:
          break;
      }

      // Clear selection
      setSelectedInvoices([]);
      setShowBulkActionDialog(false);
      fetchInvoices();
    } catch (error) {
      console.error("Bulk action failed", error);
      toast.error("Failed to perform bulk action");
    }
  };

  // Get Status Badge Function
  const getStatusBadge = (status) => {
    const statusConfig = {
      [INVOICE_STATUS.PENDING_REVIEW]: {
        color: 'bg-yellow-100 text-yellow-800',
        label: '⏳ Pending Review'
      },
      [INVOICE_STATUS.APPROVED]: {
        color: 'bg-blue-100 text-blue-800',
        label: '✅ Approved'
      },
      [INVOICE_STATUS.PENDING_PAYMENT]: {
        color: 'bg-orange-100 text-orange-800',
        label: '💰 Pending Payment'
      },
      [INVOICE_STATUS.CLEARED]: {
        color: 'bg-green-100 text-green-800',
        label: '✔️ Cleared (Paid)'
      },
      [INVOICE_STATUS.DISPUTE]: {
        color: 'bg-red-100 text-red-800',
        label: '⚠️ Dispute'
      }
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-800',
      label: status || 'Unknown'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Invoice sorting and filtering functions
  const getSortedAndFilteredInvoices = () => {
    let filtered = [...invoices];

    // Apply credit note filter
    if (invoiceFilters.isCreditNote) {
      filtered = filtered.filter(inv => inv.is_credit_note === true);
    }

    // Apply status filter
    if (invoiceFilters.status.length > 0) {
      filtered = filtered.filter(inv => invoiceFilters.status.includes(inv.status));
    }

    // Apply customer filter
    if (invoiceFilters.customer.length > 0) {
      filtered = filtered.filter(inv => invoiceFilters.customer.includes(inv.customer_id));
    }

    // Apply date range filter
    if (invoiceFilters.dateFrom) {
      const fromDate = new Date(invoiceFilters.dateFrom);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) >= fromDate);
    }

    if (invoiceFilters.dateTo) {
      const toDate = new Date(invoiceFilters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) <= toDate);
    }

    // Apply amount range filter
    if (invoiceFilters.amountFrom) {
      const minAmount = parseFloat(invoiceFilters.amountFrom);
      filtered = filtered.filter(inv => inv.total_amount >= minAmount);
    }

    if (invoiceFilters.amountTo) {
      const maxAmount = parseFloat(invoiceFilters.amountTo);
      filtered = filtered.filter(inv => inv.total_amount <= maxAmount);
    }

    // History toggle:
    // - when OFF: show everything except cleared
    // - when ON: show only cleared (paid) invoices
    if (showClearedInvoices) {
      filtered = filtered.filter(invoice => invoice.status === INVOICE_STATUS.CLEARED);
    } else {
      filtered = filtered.filter(invoice => invoice.status !== INVOICE_STATUS.CLEARED);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (invoiceSortBy) {
        case "invoice_number":
          aVal = a.invoice_number || "";
          bVal = b.invoice_number || "";
          break;
        case "order_number":
          aVal = a.order_number || "";
          bVal = b.order_number || "";
          break;
        case "customer_name":
          aVal = a.customer_name || "";
          bVal = b.customer_name || "";
          break;
        case "amount":
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case "due_date":
          aVal = new Date(a.due_date || 0);
          bVal = new Date(b.due_date || 0);
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
      }

      if (typeof aVal === 'number') {
        return invoiceSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else if (aVal instanceof Date) {
        return invoiceSortOrder === "asc" ? aVal - bVal : bVal - aVal;
      } else {
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return invoiceSortOrder === "asc" ? comparison : -comparison;
      }
    });

    return filtered;
  };

  const handleCreateCreditNote = async () => {
    if (!selectedInvoiceForCredit) return;

    try {
      const creditNoteData = {
        original_invoice_id: selectedInvoiceForCredit.id,
        reason: creditNoteForm.reason,
        amount: parseFloat(creditNoteForm.amount),
        notes: creditNoteForm.notes || ''
      };

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/credit-notes`, creditNoteData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success("Credit note created successfully");
      setShowCreditNoteDialog(false);
      setSelectedInvoiceForCredit(null);
      setCreditNoteForm({
        reason: "",
        amount: 0,
        notes: ""
      });

      // Refresh invoices to show updated status
      fetchInvoices();

    } catch (error) {
      console.error("Failed to create credit note", error);
      toast.error(error.response?.data?.detail || "Failed to create credit note");
    }
  };

  const handleSaveCreditNote = async () => {
    if (!selectedInvoiceForCredit) return;

    try {
      const creditNoteData = {
        original_invoice_id: selectedInvoiceForCredit.id,
        customer_id: selectedInvoiceForCredit.customer_id,
        customer_name: selectedInvoiceForCredit.customer_name,
        customer_email: getCustomerFinanceEmail(selectedInvoiceForCredit.customer_id),
        reason: creditNoteForm.reason,
        amount: creditNoteForm.amount,
        notes: creditNoteForm.notes,
        status: INVOICE_STATUS.PENDING_REVIEW,
        is_credit_note: true
      };

      await axios.post(`${API}/credit-notes`, creditNoteData);

      toast.success("Credit note created successfully");
      setShowCreditNoteDialog(false);
      setSelectedInvoiceForCredit(null);
      fetchInvoices();

    } catch (error) {
      console.error("Failed to create credit note", error);
      toast.error(error.response?.data?.detail || "Failed to create credit note");
    }
  };

  const getCustomerFinanceEmail = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return "";

    // Check if customer has secondary_email field
    return customer.secondary_email || customer.email;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "overview"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "orders"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab("cases")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "cases"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Cases
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "users"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab("skus")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "skus"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            SKU Management
          </button>
          <button
            onClick={() => setActiveTab("customer-pricing")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "customer-pricing"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Customer Pricing
          </button>
          <button
            onClick={() => setActiveTab("frequency-templates")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "frequency-templates"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Frequency Templates
          </button>
          <button
            onClick={() => setActiveTab("delivery-tracking")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "delivery-tracking"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Delivery Tracking
          </button>
          <button
            onClick={() => setActiveTab("calendar")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "calendar"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            📅 Calendar
          </button>
          <button
            onClick={() => handleTabChange("invoices")}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === "invoices"
              ? "text-teal-600 border-b-2 border-teal-600"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            Invoices
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Orders
                  </CardTitle>
                  <Package className="w-5 h-5 text-teal-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {stats?.total_orders || orders.length}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">All time</p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Customers
                  </CardTitle>
                  <Users className="w-5 h-5 text-teal-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {customers.filter(u => u.role === "customer").length}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Active accounts</p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Revenue
                  </CardTitle>
                  <DollarSign className="w-5 h-5 text-teal-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    ${orders.reduce((sum, order) => sum + (order.total_amount || 0), 0).toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Ready for pickup orders
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Open Cases
                  </CardTitle>
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {cases.filter(c => c.status === "open" || c.status === "in_progress").length}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Requires attention</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Trend Chart */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <TrendingUp className="w-5 h-5 text-teal-500" />
                    Revenue Trend (Last 7 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={getRevenueChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="name"
                        stroke="#6b7280"
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                        }}
                        formatter={(value, name) => {
                          if (name === "revenue") return [`$${value}`, "Revenue"];
                          return [value, "Orders"];
                        }}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return payload[0].payload.fullDate;
                          }
                          return label;
                        }}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: "20px" }}
                        formatter={(value) =>
                          value === "revenue" ? "Revenue ($)" : "Orders"
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={CHART_COLORS.primary}
                        strokeWidth={3}
                        dot={{ fill: CHART_COLORS.primary, r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="orders"
                        stroke={CHART_COLORS.secondary}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS.secondary, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Orders by Status Pie Chart */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <Package className="w-5 h-5 text-teal-500" />
                    Orders by Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const statusData = getOrdersByStatusData();
                    return statusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) =>
                              `${name}: ${(percent * 100).toFixed(0)}%`
                            }
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {statusData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  CHART_COLORS[entry.status] ||
                                  CHART_COLORS.primary
                                }
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-gray-500">
                        No order data available
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Top Customers Bar Chart */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <Users className="w-5 h-5 text-teal-500" />
                    Top 5 Customers by Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getTopCustomersData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="name"
                        stroke="#6b7280"
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                        angle={-15}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        stroke="#6b7280"
                        tick={{ fill: "#6b7280", fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                        }}
                        formatter={(value) => [`$${value}`, "Revenue"]}
                        labelFormatter={(label, payload) => {
                          if (
                            payload &&
                            payload[0] &&
                            payload[0].payload.fullName
                          ) {
                            return payload[0].payload.fullName;
                          }
                          return label;
                        }}
                      />
                      <Bar
                        dataKey="revenue"
                        fill={CHART_COLORS.primary}
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Cases Status Chart */}
              <Card className="card-hover">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-900">
                    <AlertCircle className="w-5 h-5 text-teal-500" />
                    Cases Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const casesData = getCasesData();
                    return casesData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={casesData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percent }) =>
                              `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                            }
                            outerRadius={90}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {casesData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                            }}
                            formatter={(value, name) => [value, "Cases"]}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            formatter={(value, entry) => {
                              const item = casesData.find(
                                (d) => d.name === value
                              );
                              return `${value} (${item ? item.value : 0})`;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-gray-500">
                        No case data available
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Order Management
                </h2>
                <p className="text-gray-600 mt-1">
                  Create orders and assign drivers for delivery
                </p>
              </div>
              <Dialog
                open={showOrderDialog}
                onOpenChange={(open) => {
                  setShowOrderDialog(open);
                  if (!open) {
                    setIsEditMode(false);
                    setEditingOrder(null);
                    setOrderForm({
                      customer_id: "",
                      customer_name: "",
                      customer_email: "",
                      items: [],
                      delivery_date: "",
                      pickup_address: PERMANENT_PICKUP_ADDRESS,
                      delivery_address: "",
                      special_instructions: "",
                      is_recurring: false,
                      frequency_template_id: "",
                    });
                    setOrderItems([
                      { sku_id: "", sku_name: "", quantity: 1, price: 0 },
                    ]);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    className="bg-teal-500 hover:bg-teal-600"
                    onClick={() => {
                      setIsEditMode(false);
                      setEditingOrder(null);
                      setOrderForm(prev => ({
                        ...prev,
                        delivery_date: getDefaultDeliveryDate()
                      }));
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {isEditMode ? "Edit Order" : "Create New Order"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateOrder} className="space-y-4">
                    <div>
                      <Label>Customer</Label>
                      <Select
                        value={orderForm.customer_id}
                        onValueChange={(value) =>
                          setOrderForm({ ...orderForm, customer_id: value })
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.filter(c => c.role === "customer").map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.full_name} ({customer.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Order Items</Label>
                      {orderItems.map((item, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <Select
                            value={item.sku_id}
                            onValueChange={(value) =>
                              updateOrderItem(index, "sku_id", value)
                            }
                            required
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select item" />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredSkus.map((sku) => {
                                const price =
                                  sku.customer_price !== undefined
                                    ? sku.customer_price
                                    : sku.price;
                                const gst = price * 0.1;
                                const total = price + gst;
                                return (
                                  <SelectItem key={sku.id} value={sku.id}>
                                    {sku.name} - ${price.toFixed(2)} + GST $
                                    {gst.toFixed(2)} = ${total.toFixed(2)} per
                                    unit
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateOrderItem(index, "quantity", e.target.value)
                            }
                            className="w-20"
                            placeholder="Qty"
                            required
                          />
                          {orderItems.length > 1 && (
                            <Button
                              type="button"
                              variant="destructive"
                              onClick={() => removeOrderItem(index)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addOrderItem}
                        className="mt-2"
                      >
                        Add Item
                      </Button>
                    </div>

                    <div>
                      <Label>Delivery Date</Label>
                      <Input
                        type="date"
                        value={orderForm.delivery_date}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_date: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <Label>Pickup Address</Label>
                      <Input
                        value={orderForm.pickup_address}
                        disabled
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <Label>Delivery Address</Label>
                      <Input
                        value={orderForm.delivery_address}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            delivery_address: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <Label>Special Instructions</Label>
                      <Textarea
                        value={orderForm.special_instructions}
                        onChange={(e) =>
                          setOrderForm({
                            ...orderForm,
                            special_instructions: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <Switch
                          checked={orderForm.is_recurring}
                          onCheckedChange={(checked) =>
                            setOrderForm({
                              ...orderForm,
                              is_recurring: checked,
                            })
                          }
                        />
                        <Label className="flex items-center gap-2 cursor-pointer">
                          <Repeat className="w-4 h-4 text-teal-600" />
                          Make this a recurring order
                        </Label>
                      </div>

                      {orderForm.is_recurring && (
                        <div>
                          <Label>Select Frequency Template</Label>
                          <Select
                            value={orderForm.frequency_template_id}
                            onValueChange={(value) =>
                              setOrderForm({
                                ...orderForm,
                                frequency_template_id: value,
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose recurring frequency" />
                            </SelectTrigger>
                            <SelectContent>
                              {frequencyTemplates.map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id}
                                >
                                  {template.name} - Every{" "}
                                  {template.frequency_value}{" "}
                                  {template.frequency_type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-2">
                            This order will automatically repeat based on the
                            selected frequency
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Order Summary */}
                    {orderItems.some(
                      (item) => item.sku_id && item.quantity > 0
                    ) && (
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <h3 className="font-semibold text-gray-900 mb-3">
                            Order Summary
                          </h3>
                          {(() => {
                            const subtotalExGST = orderItems.reduce(
                              (total, item) => {
                                const sku = customerSkus.length > 0
                                  ? customerSkus.find(s => s.id === item.sku_id)
                                  : filteredSkus.find(s => s.id === item.sku_id);
                                if (!sku || !item.quantity) return total;
                                const price =
                                  sku.customer_price !== undefined
                                    ? sku.customer_price
                                    : sku.price;
                                return (
                                  total + price * parseInt(item.quantity || 0)
                                );
                              },
                              0
                            );
                            const gstAmount = subtotalExGST * 0.1;
                            const totalIncGST = subtotalExGST + gstAmount;

                            return (
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    Base Amount:
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    ${subtotalExGST.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    GST (10%):
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    ${gstAmount.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                                  <span className="text-lg font-bold text-gray-900">
                                    Total Amount
                                  </span>
                                  <span className="text-2xl font-bold text-teal-600">
                                    ${totalIncGST.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                    {isEditMode && editingOrder && (
                      <div className="flex gap-2 w-full mb-3">
                        <Button
                          type="button"
                          variant="secondary"
                          className="flex-1"
                          onClick={() => generateInvoice(editingOrder)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Invoice
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleDownloadInvoicePDF(editingOrder)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                      disabled={creatingOrder || editingOrderId !== null}
                    >
                      {creatingOrder || editingOrderId ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          {isEditMode ? "Updating..." : "Creating..."}
                        </>
                      ) : isEditMode ? (
                        "Update Order"
                      ) : (
                        "Create Order"
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Search and Filter Controls */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by order #, customer name, or email..."
                        value={orderSearchQuery}
                        onChange={(e) => setOrderSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {(orderSearchQuery ||
                      orderStatusFilter.length > 0 ||
                      orderDeliveryStatusFilter.length > 0 ||
                      orderDateFilter !== "all" ||
                      orderDateFrom ||
                      orderDateTo ||
                      orderTypeFilter !== "all") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearOrderFilters}
                          className="whitespace-nowrap"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear Filters
                        </Button>
                      )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium whitespace-nowrap">
                        Sort by:
                      </Label>
                      <Select
                        value={orderSortBy}
                        onValueChange={setOrderSortBy}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at">Order Date</SelectItem>
                          <SelectItem value="delivery_date">
                            Delivery Date
                          </SelectItem>
                          <SelectItem value="order_number">
                            Order Number
                          </SelectItem>
                          <SelectItem value="customer_name">
                            Customer Name
                          </SelectItem>
                          <SelectItem value="status">Status</SelectItem>
                          <SelectItem value="total_amount">
                            Total Amount
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setOrderSortOrder(
                            orderSortOrder === "asc" ? "desc" : "asc"
                          )
                        }
                        title={
                          orderSortOrder === "asc" ? "Ascending" : "Descending"
                        }
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        {orderSortOrder === "asc" ? "↑" : "↓"}
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <Select
                        value={orderDateFilter}
                        onValueChange={setOrderDateFilter}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Dates</SelectItem>
                          <SelectItem value="today">Today</SelectItem>
                          <SelectItem value="week">This Week</SelectItem>
                          <SelectItem value="month">This Month</SelectItem>
                          <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {orderDateFilter === "custom" && (
                      <div className="flex items-center gap-2">
                        <Label className="text-sm whitespace-nowrap">
                          From:
                        </Label>
                        <Input
                          type="date"
                          value={orderDateFrom}
                          onChange={(e) => setOrderDateFrom(e.target.value)}
                          className="w-[150px]"
                        />
                        <Label className="text-sm whitespace-nowrap">To:</Label>
                        <Input
                          type="date"
                          value={orderDateTo}
                          onChange={(e) => setOrderDateTo(e.target.value)}
                          className="w-[150px]"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-gray-500" />
                      <Select
                        value={orderTypeFilter}
                        onValueChange={setOrderTypeFilter}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="regular">Regular Only</SelectItem>
                          <SelectItem value="recurring">
                            Recurring Only
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <Label className="text-sm font-medium">Status:</Label>
                    {[
                      "pending",
                      "scheduled",
                      "processing",
                      "ready_for_pickup",
                      "out_for_delivery",
                      "delivered",
                      "cancelled",
                    ].map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleOrderStatusFilter(status)}
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${orderStatusFilter.includes(status)
                          ? "bg-teal-500 text-white border-teal-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-teal-500"
                          }`}
                      >
                        {getStatusDisplayName(status)}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="w-4 h-4 text-gray-500" />
                    <Label className="text-sm font-medium">
                      Delivery Status:
                    </Label>
                    {[
                      "assigned",
                      "picked_up",
                      "out_for_delivery",
                      "delivered",
                    ].map((deliveryStatus) => (
                      <button
                        key={deliveryStatus}
                        onClick={() =>
                          toggleDeliveryStatusFilter(deliveryStatus)
                        }
                        className={`px-3 py-1 text-xs rounded-full border transition-colors ${orderDeliveryStatusFilter.includes(deliveryStatus)
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-blue-500"
                          }`}
                      >
                        {deliveryStatus.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>

                  <div className="text-sm text-gray-600">
                    Showing{" "}
                    <span className="font-semibold">
                      {getFilteredAndSortedOrders().length}
                    </span>{" "}
                    of <span className="font-semibold">{orders.length}</span>{" "}
                    orders
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Edit Requests Section */}
            {pendingEditRequests.length > 0 && (
              <Card className="mb-6 border-2 border-yellow-300 shadow-lg">
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="flex items-center gap-2 text-yellow-900">
                    <Clock className="w-5 h-5" />
                    Pending Customer Edit Requests ({pendingEditRequests.length}
                    )
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {pendingEditRequests.map((order) => (
                      <div
                        key={order.id}
                        className="p-4 bg-white border-2 border-yellow-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-lg text-gray-900">
                                {order.order_number}
                              </span>
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                                <Repeat className="w-3 h-3" />
                                Recurring Order
                              </span>
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Edit Pending
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">Customer:</span>
                                <span className="font-medium text-gray-900 ml-2">
                                  {order.customer_name}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">
                                  Requested:
                                </span>
                                <span className="font-medium text-gray-900 ml-2">
                                  {new Date(
                                    order.modification_requested_at
                                  ).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            {order.pending_modifications?.reason && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                <span className="text-gray-600 font-medium">
                                  Reason:
                                </span>
                                <p className="text-gray-800 mt-1">
                                  {order.pending_modifications.reason}
                                </p>
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => handleViewEditRequest(order)}
                            className="bg-teal-500 hover:bg-teal-600 ml-4"
                            size="sm"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Review Request
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Orders Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-w-full">
                  <table className="w-full min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Order
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Delivery
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Driver
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Assign
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getPaginatedOrders().map((order) => (
                        <tr
                          key={order.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-3">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-gray-900 text-sm">
                                {order.order_number}
                              </span>
                              <div className="flex gap-1 flex-wrap">
                                {order.is_recurring && (
                                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 flex items-center gap-1">
                                    <Repeat className="w-3 h-3" />
                                  </span>
                                )}
                                {order.is_locked && (
                                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                    <Lock className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {order.customer_name}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {order.customer_email}
                              </p>
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="text-xs space-y-1">
                              {order.items.slice(0, 2).map((item, idx) => (
                                <p key={idx} className="text-gray-700">
                                  {item.sku_name}{" "}
                                  <span className="text-gray-500">
                                    ×{item.quantity}
                                  </span>
                                </p>
                              ))}
                              {order.items.length > 2 && (
                                <p className="text-xs text-gray-500">
                                  +{order.items.length - 2} more
                                </p>
                              )}
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="text-xs">
                              <p className="text-gray-700">
                                {new Date(
                                  order.delivery_date
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="text-sm font-bold text-teal-600">
                              ${(order.total_amount * 1.1).toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              Inc. GST
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <Select
                              value={order.status}
                              onValueChange={(value) =>
                                handleUpdateOrderStatus(order.id, value)
                              }
                              disabled={updatingStatusOrderId === order.id}
                            >
                              <SelectTrigger className="w-full min-w-[120px]">
                                {updatingStatusOrderId === order.id ? (
                                  <span className="flex items-center text-xs">
                                    <Clock className="w-3 h-3 mr-2 animate-spin" />
                                    Updating...
                                  </span>
                                ) : (
                                  <SelectValue placeholder="Select Status">
                                    {order.status === "pending" ? "Pending" :
                                      order.status === "confirmed" ? "Confirmed" :
                                        order.status === "scheduled" ? "Scheduled" :
                                          order.status === "processing" ? "Processing" :
                                            order.status === "ready_for_pickup" ? "Ready for Pickup" :
                                              order.status === "out_for_delivery" ? "Out for Delivery" :
                                                order.status === "delivered" ? "Delivered" :
                                                  order.status === "cancelled" ? "Cancelled" :
                                                    order.status || "Select Status"}
                                  </SelectValue>
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="scheduled">
                                  Scheduled
                                </SelectItem>
                                <SelectItem value="processing">
                                  Processing
                                </SelectItem>
                                <SelectItem value="ready_for_pickup">
                                  Ready for Pickup
                                </SelectItem>
                                <SelectItem value="out_for_delivery">
                                  Out for Delivery
                                </SelectItem>
                                <SelectItem value="delivered">
                                  Delivered
                                </SelectItem>
                                <SelectItem value="cancelled">
                                  Cancelled
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </td>

                          <td className="px-3 py-3 text-xs text-gray-600">
                            {order.driver_name || (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>

                          <td className="px-3 py-3">
                            {order.driver_id ? (
                              <div className="flex items-center gap-1">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUnassignDriver(order.id)}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 w-7 p-0"
                                  title="Unassign driver"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Select
                                  value={selectedDriver[order.id] || ""}
                                  onValueChange={(value) =>
                                    setSelectedDriver({
                                      ...selectedDriver,
                                      [order.id]: value,
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-28 h-7 text-xs">
                                    <SelectValue placeholder="Driver" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map((driver) => (
                                      <SelectItem
                                        key={driver.id}
                                        value={driver.id}
                                      >
                                        {driver.full_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  onClick={() => handleAssignDriver(order.id)}
                                  className="bg-teal-500 hover:bg-teal-600 h-7 w-7 p-0"
                                  title="Assign"
                                >
                                  <Truck className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </td>

                          <td className="px-3 py-3">
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditOrder(order)}
                                disabled={editingOrderId === order.id}
                                className="border-teal-300 text-teal-600 hover:bg-teal-50 h-7 w-7 p-0"
                                title="Edit order"
                              >
                                {editingOrderId === order.id ? (
                                  <Clock className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Edit className="w-3 h-3" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteOrder(order.id)}
                                disabled={deletingOrderId === order.id}
                                className="border-red-300 text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                title="Delete order"
                              >
                                {deletingOrderId === order.id ? (
                                  <Clock className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  order.is_locked
                                    ? handleUnlockOrder(order.id)
                                    : handleLockOrder(order.id)
                                }
                                className={`h-7 w-7 p-0 ${order.is_locked
                                  ? "border-green-300 text-green-600 hover:bg-green-50"
                                  : "border-yellow-300 text-yellow-600 hover:bg-yellow-50"
                                  }`}
                                title={
                                  order.is_locked
                                    ? "Unlock order"
                                    : "Lock order"
                                }
                              >
                                {order.is_locked ? (
                                  <Unlock className="w-3 h-3" />
                                ) : (
                                  <Lock className="w-3 h-3" />
                                )}
                              </Button>
                              {/* Mark as Delivered Button */}
                              {/* {order.status !== "delivered" && order.delivery_status !== "delivered" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMarkAsDelivered(order.id)}
                                  className="border-green-300 text-green-600 hover:bg-green-50 h-7 w-7 p-0"
                                  title="Mark as delivered and create invoice"
                                >
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                              )} */}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {getTotalPages() > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(
                        currentPage * itemsPerPage,
                        getFilteredAndSortedOrders().length
                      )}{" "}
                      of {getFilteredAndSortedOrders().length} orders
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <div className="flex gap-1">
                        {[...Array(getTotalPages())].map((_, idx) => {
                          const pageNum = idx + 1;
                          if (
                            pageNum === 1 ||
                            pageNum === getTotalPages() ||
                            (pageNum >= currentPage - 1 &&
                              pageNum <= currentPage + 1)
                          ) {
                            return (
                              <Button
                                key={pageNum}
                                variant={
                                  currentPage === pageNum
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                className={
                                  currentPage === pageNum
                                    ? "bg-teal-500 hover:bg-teal-600"
                                    : ""
                                }
                              >
                                {pageNum}
                              </Button>
                            );
                          } else if (
                            pageNum === currentPage - 2 ||
                            pageNum === currentPage + 2
                          ) {
                            return (
                              <span key={pageNum} className="px-2 py-1">
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {getFilteredAndSortedOrders().length === 0 && (
                  <div className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      No Orders Found
                    </h3>
                    <p className="text-gray-600">
                      {orders.length === 0
                        ? "Create your first order to get started"
                        : "No orders match your current filters"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Cases Tab - Same as before */}
        {activeTab === "cases" && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Case Management
            </h2>

            {/* Search and Filter Controls */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="space-y-4">
                  {/* Search and Sort Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Search Cases
                      </Label>
                      <Input
                        placeholder="Search by case number, customer, or subject..."
                        value={caseSearchQuery}
                        onChange={(e) => setCaseSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={caseSortBy}
                          onValueChange={setCaseSortBy}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="created_at">
                              Created Date
                            </SelectItem>
                            <SelectItem value="case_number">
                              Case Number
                            </SelectItem>
                            <SelectItem value="customer_name">
                              Customer
                            </SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="priority">Priority</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCaseSortOrder(
                              caseSortOrder === "asc" ? "desc" : "asc"
                            )
                          }
                          className="px-3"
                        >
                          {caseSortOrder === "asc" ? "↑" : "↓"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter by Status
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["open", "in_progress", "resolved", "closed"].map(
                        (status) => (
                          <Button
                            key={status}
                            variant={
                              caseStatusFilter.includes(status)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => toggleCaseStatusFilter(status)}
                            className={
                              caseStatusFilter.includes(status)
                                ? "bg-blue-500 hover:bg-blue-600"
                                : ""
                            }
                          >
                            {status.replace("_", " ")}
                          </Button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Priority Filter */}
                  <div>
                    <Label className="text-sm font-medium mb-2">
                      Filter by Priority
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["low", "medium", "high", "urgent"].map((priority) => (
                        <Button
                          key={priority}
                          variant={
                            casePriorityFilter.includes(priority)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => toggleCasePriorityFilter(priority)}
                          className={
                            casePriorityFilter.includes(priority)
                              ? "bg-orange-500 hover:bg-orange-600"
                              : ""
                          }
                        >
                          {priority}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Date Range Filter */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        From Date
                      </Label>
                      <Input
                        type="date"
                        value={caseDateFrom}
                        onChange={(e) => setCaseDateFrom(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        To Date
                      </Label>
                      <Input
                        type="date"
                        value={caseDateTo}
                        onChange={(e) => setCaseDateTo(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Results and Clear */}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing {getFilteredAndSortedCases().length} of{" "}
                      {cases.length} cases
                    </p>
                    {(caseSearchQuery ||
                      caseStatusFilter.length > 0 ||
                      casePriorityFilter.length > 0 ||
                      caseDateFrom ||
                      caseDateTo ||
                      caseSortBy !== "created_at" ||
                      caseSortOrder !== "desc") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearCaseFilters}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cases Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Case Details
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Subject & Description
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredAndSortedCases().map((caseItem) => (
                        <tr
                          key={caseItem.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-gray-900 text-base">
                                {caseItem.case_number}
                              </span>
                              <span className="text-xs text-gray-500">
                                Created:{" "}
                                {new Date(
                                  caseItem.created_at
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {caseItem.customer_name}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {caseItem.customer_email}
                              </p>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="max-w-md">
                              <p className="font-semibold text-gray-900 text-sm mb-1">
                                {caseItem.subject}
                              </p>
                              <p className="text-gray-600 text-xs line-clamp-2">
                                {caseItem.description}
                              </p>
                              {caseItem.resolution && (
                                <div className="mt-2 p-2 bg-green-50 rounded">
                                  <p className="text-xs font-semibold text-green-800">
                                    Resolution:
                                  </p>
                                  <p className="text-xs text-green-700 line-clamp-2">
                                    {caseItem.resolution}
                                  </p>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full inline-block ${caseItem.priority === "urgent"
                                ? "bg-red-100 text-red-800"
                                : caseItem.priority === "high"
                                  ? "bg-orange-100 text-orange-800"
                                  : caseItem.priority === "medium"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {caseItem.priority}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full inline-block ${caseItem.status === "open"
                                ? "bg-yellow-100 text-yellow-800"
                                : caseItem.status === "in_progress"
                                  ? "bg-blue-100 text-blue-800"
                                  : caseItem.status === "resolved"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {caseItem.status.replace("_", " ")}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <Button
                              onClick={() => {
                                setSelectedCase(caseItem);
                                setCaseUpdate({
                                  status: caseItem.status,
                                  resolution: caseItem.resolution || "",
                                  priority: caseItem.priority,
                                });
                                setShowCaseDialog(true);
                              }}
                              size="sm"
                              className="bg-teal-500 hover:bg-teal-600"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Update
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State */}
                {getFilteredAndSortedCases().length === 0 && (
                  <div className="p-12 text-center">
                    <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      No Cases Found
                    </h3>
                    <p className="text-gray-600">
                      {cases.length === 0
                        ? "No cases have been submitted yet"
                        : "No cases match your current filters"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={showCaseDialog} onOpenChange={setShowCaseDialog}>
              <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle>Update Case</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateCase} className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={caseUpdate.status}
                      onValueChange={(value) =>
                        setCaseUpdate({ ...caseUpdate, status: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={caseUpdate.priority}
                      onValueChange={(value) =>
                        setCaseUpdate({ ...caseUpdate, priority: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Resolution</Label>
                    <Textarea
                      value={caseUpdate.resolution}
                      onChange={(e) =>
                        setCaseUpdate({
                          ...caseUpdate,
                          resolution: e.target.value,
                        })
                      }
                      rows={4}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-teal-500 hover:bg-teal-600"
                    disabled={updatingCaseId !== null}
                  >
                    {updatingCaseId ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Case"
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                User Management
              </h2>
              <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={userForm.full_name}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            full_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={userForm.email}
                        onChange={(e) =>
                          setUserForm({ ...userForm, email: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={userForm.password}
                        onChange={(e) =>
                          setUserForm({ ...userForm, password: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={userForm.role}
                        onValueChange={(value) =>
                          setUserForm({ ...userForm, role: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {userForm.role === 'customer' && (
                      <div>
                        <Label>Secondary Email</Label>
                        <Input
                          type="email"
                          value={userForm.secondary_email}
                          onChange={(e) =>
                            setUserForm({
                              ...userForm,
                              secondary_email: e.target.value,
                            })
                          }
                          placeholder="Secondary email (optional)"
                        />
                      </div>
                    )}
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={userForm.phone}
                        onChange={(e) =>
                          setUserForm({ ...userForm, phone: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={userForm.address}
                        onChange={(e) =>
                          setUserForm({ ...userForm, address: e.target.value })
                        }
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                    >
                      Create User
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Search and Filter Controls */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-4">
                  {/* Search and Sort Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Search Users
                      </Label>
                      <Input
                        placeholder="Search by name or email..."
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={userSortBy}
                          onValueChange={setUserSortBy}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="name">Name</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="role">Role</SelectItem>
                            <SelectItem value="created_at">
                              Created Date
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setUserSortOrder(
                              userSortOrder === "asc" ? "desc" : "asc"
                            )
                          }
                          className="px-3"
                        >
                          {userSortOrder === "asc" ? "↑" : "↓"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Row */}
                  <div>
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter by Role
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {["owner", "admin", "customer", "driver"].map((role) => (
                        <Button
                          key={role}
                          variant={
                            userRoleFilter.includes(role)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => toggleUserRoleFilter(role)}
                          className={
                            userRoleFilter.includes(role)
                              ? "bg-teal-500 hover:bg-teal-600"
                              : ""
                          }
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Results and Clear */}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing {getFilteredAndSortedUsers().length} of{" "}
                      {customers.length} users
                    </p>
                    {(userSearchQuery ||
                      userRoleFilter.length > 0 ||
                      userSortBy !== "created_at" ||
                      userSortOrder !== "desc") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearUserFilters}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          User Details
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getPaginatedUsers().map((customer) => (
                        <tr
                          key={customer.id}
                          className={`hover:bg-gray-50 transition-colors ${!customer.is_active ? "opacity-60 bg-gray-50" : ""
                            }`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-gray-900 text-base">
                                {customer.full_name}
                              </span>
                              <span className="font-mono text-xs text-gray-500">
                                {customer.id}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {customer.email}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {customer.phone || "N/A"}
                              </p>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full inline-block ${customer.role === "owner"
                                ? "bg-purple-100 text-purple-800"
                                : customer.role === "admin"
                                  ? "bg-blue-100 text-blue-800"
                                  : customer.role === "driver"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                            >
                              {customer.role.charAt(0).toUpperCase() +
                                customer.role.slice(1)}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1 ${customer.is_active !== false
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                                }`}
                            >
                              {customer.is_active !== false ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3" /> Active
                                </>
                              ) : (
                                <>
                                  <Ban className="w-3 h-3" /> Disabled
                                </>
                              )}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">
                              {new Date(
                                customer.created_at
                              ).toLocaleDateString()}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex gap-2 justify-center">
                              {/* Email Settings Button - Only for customers */}
                              {customer.role === "customer" && (
                                <Button
                                  onClick={() => {
                                    setSelectedCustomerForEmail(customer);
                                    setShowEmailSettingsDialog(true);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                                  title="Email Settings"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              {(customer.role === "customer" ||
                                customer.role === "driver") && (
                                  <Button
                                    onClick={() => openUserEditDialog(customer)}
                                    variant="outline"
                                    size="sm"
                                    className="border-teal-300 text-teal-600 hover:bg-teal-50"
                                    title="Edit User"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                              <Button
                                onClick={() =>
                                  openPasswordResetDialog(customer)
                                }
                                variant="outline"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                                disabled={
                                  resettingPasswordUserId === customer.id
                                }
                                title="Reset Password"
                              >
                                {resettingPasswordUserId === customer.id ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Key className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                onClick={() => handleToggleUserStatus(customer)}
                                variant="outline"
                                size="sm"
                                className={`${customer.is_active !== false
                                  ? "text-red-600 hover:text-red-800 border-red-300 hover:bg-red-50"
                                  : "text-green-600 hover:text-green-800 border-green-300 hover:bg-green-50"
                                  }`}
                                title={
                                  customer.is_active !== false
                                    ? "Disable User"
                                    : "Enable User"
                                }
                                disabled={togglingUserId === customer.id}
                              >
                                {togglingUserId === customer.id ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : customer.is_active !== false ? (
                                  <Ban className="w-4 h-4" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State */}
                {getPaginatedUsers().length === 0 && (
                  <div className="p-12 text-center">
                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      No Users Found
                    </h3>
                    <p className="text-gray-600">
                      {customers.length === 0
                        ? "No users in the system yet"
                        : "No users match your current filters"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination Controls */}
            {getUserTotalPages() > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Showing {(userCurrentPage - 1) * userItemsPerPage + 1} to{" "}
                  {Math.min(
                    userCurrentPage * userItemsPerPage,
                    getFilteredAndSortedUsers().length
                  )}{" "}
                  of {getFilteredAndSortedUsers().length} users
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUserPageChange(userCurrentPage - 1)}
                    disabled={userCurrentPage === 1}
                  >
                    Previous
                  </Button>

                  <div className="flex gap-1">
                    {Array.from(
                      { length: Math.min(5, getUserTotalPages()) },
                      (_, i) => {
                        let pageNum;
                        const totalPages = getUserTotalPages();

                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (userCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (userCurrentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = userCurrentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={
                              userCurrentPage === pageNum
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => handleUserPageChange(pageNum)}
                            className={
                              userCurrentPage === pageNum
                                ? "bg-teal-500 hover:bg-teal-600"
                                : ""
                            }
                          >
                            {pageNum}
                          </Button>
                        );
                      }
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUserPageChange(userCurrentPage + 1)}
                    disabled={userCurrentPage === getUserTotalPages()}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* Password Reset Dialog */}
            <Dialog
              open={showPasswordDialog}
              onOpenChange={setShowPasswordDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <Label className="text-gray-700">User</Label>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedUser?.full_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedUser?.email}
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Password must be at least 6 characters long
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This will immediately change the
                      user's password. The user will need to use this new
                      password to log in.
                    </p>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPasswordDialog(false)}
                      disabled={resettingPasswordUserId}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={resettingPasswordUserId}
                    >
                      {resettingPasswordUserId ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* User Edit Dialog */}
            <Dialog
              open={showUserEditDialog}
              onOpenChange={setShowUserEditDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit User Details</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveUserEdit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={userEditForm.full_name}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            full_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={userEditForm.email}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            email: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={userEditForm.phone}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            phone: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={userEditForm.address}
                        onChange={(e) =>
                          setUserEditForm({
                            ...userEditForm,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={userEditForm.role}
                        onValueChange={(v) =>
                          setUserEditForm({ ...userEditForm, role: v })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {userEditForm.role === 'customer' && (
                      <div>
                        <Label>Secondary Email</Label>
                        <Input
                          type="email"
                          value={userEditForm.secondary_email}
                          onChange={(e) =>
                            setUserEditForm({
                              ...userEditForm,
                              secondary_email: e.target.value,
                            })
                          }
                          placeholder="Secondary email (optional)"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowUserEditDialog(false)}
                      disabled={savingUserEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-teal-600 hover:bg-teal-700"
                      disabled={savingUserEdit}
                    >
                      {savingUserEdit ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* SKUs Tab */}
        {activeTab === "skus" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                SKU Management
              </h2>
              <Dialog
                open={showSkuDialog}
                onOpenChange={(open) => {
                  setShowSkuDialog(open);
                  if (!open) {
                    setEditingSku(null);
                    setSkuForm({
                      name: "",
                      category: "",
                      price: "",
                      unit: "",
                      description: "",
                    });
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    className="bg-teal-500 hover:bg-teal-600"
                    data-testid="create-sku-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create SKU
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingSku ? "Edit SKU" : "Create New SKU"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateSku} className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={skuForm.name}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, name: e.target.value })
                        }
                        required
                        data-testid="sku-name-input"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input
                        value={skuForm.category}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, category: e.target.value })
                        }
                        required
                        data-testid="sku-category-input"
                      />
                    </div>
                    <div>
                      <Label>Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={skuForm.price}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, price: e.target.value })
                        }
                        required
                        data-testid="sku-price-input"
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Input
                        value={skuForm.unit}
                        onChange={(e) =>
                          setSkuForm({ ...skuForm, unit: e.target.value })
                        }
                        required
                        placeholder="e.g., kg, item, piece"
                        data-testid="sku-unit-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={skuForm.description}
                        onChange={(e) =>
                          setSkuForm({
                            ...skuForm,
                            description: e.target.value,
                          })
                        }
                        data-testid="sku-description-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-teal-500 hover:bg-teal-600"
                      disabled={creatingSku}
                      data-testid="sku-submit-btn"
                    >
                      {creatingSku ? (
                        <>
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                          {editingSku ? "Updating..." : "Creating..."}
                        </>
                      ) : editingSku ? (
                        "Update SKU"
                      ) : (
                        "Create SKU"
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Search and Filter Controls */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="space-y-4">
                  {/* Search and Sort Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Search SKUs
                      </Label>
                      <Input
                        placeholder="Search by name or description..."
                        value={skuSearchQuery}
                        onChange={(e) => setSkuSearchQuery(e.target.value)}
                        className="w-full"
                        data-testid="sku-search-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4" />
                        Sort By
                      </Label>
                      <div className="flex gap-2">
                        <Select value={skuSortBy} onValueChange={setSkuSortBy}>
                          <SelectTrigger data-testid="sku-sort-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="name">Name</SelectItem>
                            <SelectItem value="category">Category</SelectItem>
                            <SelectItem value="price">Price</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSkuSortOrder(
                              skuSortOrder === "asc" ? "desc" : "asc"
                            )
                          }
                          className="px-3"
                          data-testid="sku-sort-order-btn"
                        >
                          {skuSortOrder === "asc" ? "↑" : "↓"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Filter Row */}
                  {getUniqueCategories().length > 0 && (
                    <div>
                      <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        Filter by Category
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {getUniqueCategories().map((category) => (
                          <Button
                            key={category}
                            variant={
                              skuCategoryFilter.includes(category)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => toggleSkuCategoryFilter(category)}
                            className={
                              skuCategoryFilter.includes(category)
                                ? "bg-teal-500 hover:bg-teal-600"
                                : ""
                            }
                            data-testid={`sku-category-filter-${category}`}
                          >
                            {category}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Results and Clear */}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <p className="text-sm text-gray-600">
                      Showing {getFilteredAndSortedSkus().length} of{" "}
                      {skus.length} SKUs
                    </p>
                    {(skuSearchQuery ||
                      skuCategoryFilter.length > 0 ||
                      skuSortBy !== "name" ||
                      skuSortOrder !== "asc") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSkuFilters}
                          data-testid="clear-sku-filters-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Clear Filters
                        </Button>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SKUs Table - Professional Full-Width Layout */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="skus-list">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          SKU Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Unit
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Price (Inc. GST)
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredAndSortedSkus().map((sku) => (
                        <tr
                          key={sku.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {/* SKU Name */}
                          <td className="px-6 py-4">
                            <span className="font-bold text-gray-900 text-base">
                              {sku.name}
                            </span>
                          </td>

                          {/* Category */}
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-800 inline-block">
                              {sku.category}
                            </span>
                          </td>

                          {/* Description */}
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-600 max-w-xs line-clamp-2">
                              {sku.description || "No description"}
                            </p>
                          </td>

                          {/* Unit */}
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-700">
                              {sku.unit}
                            </span>
                          </td>

                          {/* Price */}
                          <td className="px-6 py-4">
                            {sku.has_custom_pricing ? (
                              <div className="flex flex-col space-y-0.5">
                                <div className="flex items-center gap-1 text-xs text-gray-400 line-through">
                                  Base: ${(sku.price * 1.1).toFixed(2)}
                                </div>
                                <div className="text-xs font-semibold text-teal-600">
                                  CUSTOM PRICING
                                </div>
                                <div className="text-xs text-gray-600">
                                  Custom: ${sku.customer_price.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-600">
                                  GST (10%): $
                                  {(sku.customer_price * 0.1).toFixed(2)}
                                </div>
                                <div className="text-sm font-bold text-teal-600 pt-1 border-t">
                                  ${(sku.customer_price * 1.1).toFixed(2)}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col space-y-0.5">
                                <div className="text-xs text-gray-600">
                                  Base: ${sku.price.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-600">
                                  GST (10%): ${(sku.price * 0.1).toFixed(2)}
                                </div>
                                <div className="text-sm font-bold text-teal-600 pt-1 border-t">
                                  ${(sku.price * 1.1).toFixed(2)}
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <div className="flex gap-2 justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSku(sku)}
                                className="border-teal-300 text-teal-600 hover:bg-teal-50"
                                title="Edit SKU"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteSku(sku.id)}
                                disabled={deletingSkuId === sku.id}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                title="Delete SKU"
                              >
                                {deletingSkuId === sku.id ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State */}
                {getFilteredAndSortedSkus().length === 0 && (
                  <div className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      No SKUs Found
                    </h3>
                    <p className="text-gray-600">
                      {skus.length === 0
                        ? "Create your first SKU to get started"
                        : "No SKUs match your current filters"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Customer Pricing
              </h2>
              <div className="max-w-md">
                <Label>Select Customer</Label>
                <Select
                  value={selectedCustomer?.id || ""}
                  onValueChange={handleSelectCustomer}
                >
                  <SelectTrigger data-testid="pricing-customer-select">
                    <SelectValue placeholder="Choose a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.full_name} ({customer.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedCustomer && (
              <>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {selectedCustomer.full_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedCustomer.email}
                      </p>
                    </div>
                    <Dialog
                      open={showPricingDialog}
                      onOpenChange={setShowPricingDialog}
                    >
                      <DialogTrigger asChild>
                        <Button
                          className="bg-teal-500 hover:bg-teal-600"
                          size="sm"
                          data-testid="add-pricing-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Custom Pricing
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Custom Pricing</DialogTitle>
                        </DialogHeader>
                        <form
                          onSubmit={handleCreatePricing}
                          className="space-y-4"
                        >
                          <div>
                            <Label>Select SKU</Label>
                            <Select
                              value={pricingForm.sku_id}
                              onValueChange={(value) =>
                                setPricingForm({
                                  ...pricingForm,
                                  sku_id: value,
                                })
                              }
                              required
                            >
                              <SelectTrigger data-testid="pricing-sku-select">
                                <SelectValue placeholder="Choose SKU" />
                              </SelectTrigger>
                              <SelectContent>
                                {skus
                                  .filter(
                                    (sku) =>
                                      !skusWithPricing.find(
                                        (s) => s.id === sku.id && s.custom_price
                                      )
                                  )
                                  .map((sku) => {
                                    const gst = sku.price * 0.1;
                                    const total = sku.price + gst;
                                    return (
                                      <SelectItem key={sku.id} value={sku.id}>
                                        {sku.name} - ${sku.price.toFixed(2)} +
                                        GST ${gst.toFixed(2)} = $
                                        {total.toFixed(2)}
                                      </SelectItem>
                                    );
                                  })}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Custom Price</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={pricingForm.custom_price}
                              onChange={(e) =>
                                setPricingForm({
                                  ...pricingForm,
                                  custom_price: e.target.value,
                                })
                              }
                              required
                              data-testid="pricing-price-input"
                            />
                          </div>
                          <Button
                            type="submit"
                            className="w-full bg-teal-500 hover:bg-teal-600"
                            disabled={creatingPricing}
                            data-testid="pricing-submit-btn"
                          >
                            {creatingPricing ? (
                              <>
                                <Clock className="w-4 h-4 mr-2 animate-spin" />
                                Adding...
                              </>
                            ) : (
                              "Add Pricing"
                            )}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <div className="space-y-3" data-testid="pricing-list">
                  {skusWithPricing
                    .filter((sku) => sku.custom_price)
                    .map((sku) => (
                      <Card key={sku.id} className="card-hover">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">
                                {sku.name}
                              </h4>
                              <div className="grid grid-cols-2 gap-4 mt-2">
                                <div className="p-2 bg-gray-50 rounded">
                                  <p className="text-xs text-gray-600 mb-1">
                                    Default Price:
                                  </p>
                                  <div className="space-y-0.5 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        Base:
                                      </span>
                                      <span className="font-medium">
                                        ${sku.price.toFixed(2)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">
                                        GST (10%):
                                      </span>
                                      <span className="font-medium">
                                        ${(sku.price * 0.1).toFixed(2)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between pt-1 border-t">
                                      <span className="font-semibold">
                                        Total:
                                      </span>
                                      <span className="font-bold text-gray-900">
                                        ${(sku.price * 1.1).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {sku.custom_price && (
                                  <div className="p-2 bg-teal-50 rounded border border-teal-200">
                                    <p className="text-xs text-teal-700 mb-1 font-medium">
                                      Custom Price:
                                    </p>
                                    <div className="space-y-0.5 text-xs">
                                      <div className="flex justify-between">
                                        <span className="text-teal-700">
                                          Base:
                                        </span>
                                        <span className="font-medium">
                                          ${sku.custom_price.toFixed(2)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-teal-700">
                                          GST (10%):
                                        </span>
                                        <span className="font-medium">
                                          ${(sku.custom_price * 0.1).toFixed(2)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between pt-1 border-t border-teal-300">
                                        <span className="font-semibold text-teal-900">
                                          Total:
                                        </span>
                                        <span className="font-bold text-teal-600">
                                          ${(sku.custom_price * 1.1).toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            {sku.custom_price && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleDeletePricing(sku.pricing_id)
                                }
                                disabled={deletingPricingId === sku.pricing_id}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                {deletingPricingId === sku.pricing_id ? (
                                  <>
                                    <Clock className="w-4 h-4 mr-1 animate-spin" />
                                    Removing...
                                  </>
                                ) : (
                                  <>
                                    <X className="w-4 h-4 mr-1" />
                                    Remove
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>

                {selectedCustomer &&
                  skusWithPricing.filter((sku) => sku.custom_price).length ===
                  0 && (
                    <Card className="mt-6">
                      <CardContent className="p-12 text-center">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">
                          No custom pricing set for this customer
                        </p>
                        <p className="text-sm text-gray-500">
                          Click "Add Custom Pricing" above to set custom pricing
                          for this customer
                        </p>
                      </CardContent>
                    </Card>
                  )}
              </>
            )}

            {!selectedCustomer && (
              <Card>
                <CardContent className="p-12 text-center">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    Select a customer to manage pricing
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Invoice Management</h2>
                  <p className="text-gray-600 mt-1">
                    {showClearedInvoices
                      ? "Showing all invoices including cleared (paid) invoices"
                      : "Showing all invoices (cleared invoices are filtered out)"}
                  </p>
                </div>

                <div className="flex gap-2">
                  {/* Credit Note Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowCreditNoteDialog(true)}
                    className="flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    Create Credit Note
                  </Button>

                  {/* Toggle Cleared Invoices / History */}
                  <Button
                    variant="outline"
                    onClick={() => setShowClearedInvoices(!showClearedInvoices)}
                    className="flex items-center gap-2"
                  >
                    {showClearedInvoices ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Hide Paid (History)
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Show Paid (History)
                      </>
                    )}
                  </Button>

                  {/* Filter Toggle Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowInvoiceFilters(!showInvoiceFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </Button>
                </div>
              </div>

              {/* Invoice Filters Panel */}
              {showInvoiceFilters && (
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Status Filter */}
                      <div>
                        <Label>Status</Label>
                        <Select
                          value={invoiceFilters.status[0] || "all"}
                          onValueChange={(value) => {
                            setInvoiceFilters({
                              ...invoiceFilters,
                              status: value === "all" ? [] : [value]
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Filter by status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value={INVOICE_STATUS.PENDING_REVIEW}>⏳ Pending Review</SelectItem>
                            <SelectItem value={INVOICE_STATUS.APPROVED}>✅ Approved</SelectItem>
                            <SelectItem value={INVOICE_STATUS.PENDING_PAYMENT}>💰 Pending Payment</SelectItem>
                            <SelectItem value={INVOICE_STATUS.CLEARED}>✔️ Cleared (Paid)</SelectItem>
                            <SelectItem value={INVOICE_STATUS.DISPUTE}>⚠️ Dispute</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Customer Filter */}
                      <div>
                        <Label>Customer</Label>
                        <Select
                          value={invoiceFilters.customer[0] || "all"}
                          onValueChange={(value) => {
                            setInvoiceFilters({
                              ...invoiceFilters,
                              customer: value === "all" ? [] : [value]
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Filter by customer" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Customers</SelectItem>
                            {customers.filter(c => c.role === 'customer').map(customer => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Range */}
                      <div>
                        <Label>Date From</Label>
                        <Input
                          type="date"
                          value={invoiceFilters.dateFrom}
                          onChange={(e) => setInvoiceFilters({
                            ...invoiceFilters,
                            dateFrom: e.target.value
                          })}
                        />
                      </div>

                      <div>
                        <Label>Date To</Label>
                        <Input
                          type="date"
                          value={invoiceFilters.dateTo}
                          onChange={(e) => setInvoiceFilters({
                            ...invoiceFilters,
                            dateTo: e.target.value
                          })}
                        />
                      </div>

                      {/* Amount Range */}
                      <div>
                        <Label>Amount From</Label>
                        <Input
                          type="number"
                          placeholder="Min amount"
                          value={invoiceFilters.amountFrom}
                          onChange={(e) => setInvoiceFilters({
                            ...invoiceFilters,
                            amountFrom: e.target.value
                          })}
                        />
                      </div>

                      <div>
                        <Label>Amount To</Label>
                        <Input
                          type="number"
                          placeholder="Max amount"
                          value={invoiceFilters.amountTo}
                          onChange={(e) => setInvoiceFilters({
                            ...invoiceFilters,
                            amountTo: e.target.value
                          })}
                        />
                      </div>

                      {/* Credit Notes Filter */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="credit-notes"
                          checked={invoiceFilters.isCreditNote}
                          onCheckedChange={(checked) => setInvoiceFilters({
                            ...invoiceFilters,
                            isCreditNote: !!checked
                          })}
                        />
                        <Label htmlFor="credit-notes">Show Credit Notes Only</Label>
                      </div>

                      {/* Clear Filters */}
                      <div className="flex items-end">
                        <Button
                          variant="outline"
                          onClick={() => setInvoiceFilters({
                            status: [],
                            customer: [],
                            dateFrom: "",
                            dateTo: "",
                            amountFrom: "",
                            amountTo: "",
                            isCreditNote: false
                          })}
                          className="w-full"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sorting Controls */}
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label>Sort by:</Label>
                      <Select value={invoiceSortBy} onValueChange={setInvoiceSortBy}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="created_at">Invoice Date</SelectItem>
                          <SelectItem value="due_date">Due Date</SelectItem>
                          <SelectItem value="invoice_number">Invoice Number</SelectItem>
                          <SelectItem value="order_number">Order Number</SelectItem>
                          <SelectItem value="customer_name">Customer Name</SelectItem>
                          <SelectItem value="amount">Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      variant="outline"
                      onClick={() => setInvoiceSortOrder(invoiceSortOrder === "asc" ? "desc" : "asc")}
                      className="flex items-center gap-2"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${invoiceSortOrder === "asc" ? "rotate-180" : ""}`} />
                      {invoiceSortOrder === "asc" ? "Ascending" : "Descending"}
                    </Button>

                    <div className="ml-auto text-sm text-gray-600">
                      Showing {getSortedAndFilteredInvoices().length} of {invoices.length} invoices
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invoice Status Summary Cards - Updated */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                {/* Pending for Review */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Pending Review</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {invoices.filter(i => i.status === INVOICE_STATUS.PENDING_REVIEW).length}
                        </p>
                      </div>
                      <Clock className="w-8 h-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Approved */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Approved</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {invoices.filter(i => i.status === INVOICE_STATUS.APPROVED).length}
                        </p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Pending Payment */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Pending Payment</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {invoices.filter(i => i.status === INVOICE_STATUS.PENDING_PAYMENT).length}
                        </p>
                      </div>
                      <DollarSign className="w-8 h-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Cleared */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Cleared</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {invoices.filter(i => i.status === INVOICE_STATUS.CLEARED).length}
                        </p>
                      </div>
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>

                {/* Dispute */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Dispute</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {invoices.filter(i => i.status === INVOICE_STATUS.DISPUTE).length}
                        </p>
                      </div>
                      <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Invoice Table */}
            <Card>
              <CardContent className="p-0">
                {/* Bulk Actions Bar */}
                {selectedInvoices.length > 0 && (
                  <div className="p-4 bg-blue-50 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedInvoices.length === getSortedAndFilteredInvoices().length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedInvoices(getSortedAndFilteredInvoices().map(inv => inv.id));
                          } else {
                            setSelectedInvoices([]);
                          }
                        }}
                      />
                      <span className="text-sm font-medium">
                        {selectedInvoices.length} invoice(s) selected
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Select value={bulkAction} onValueChange={setBulkAction}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Bulk actions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="send_email">Send Email</SelectItem>
                          <SelectItem value="mark_paid">Mark as Paid</SelectItem>
                          <SelectItem value="download_pdf">Download PDFs</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={() => setShowBulkActionDialog(true)} size="sm">
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedInvoices([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        {/* Checkbox Column */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          <Checkbox
                            checked={selectedInvoices.length === getSortedAndFilteredInvoices().length && getSortedAndFilteredInvoices().length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedInvoices(getSortedAndFilteredInvoices().map(inv => inv.id));
                              } else {
                                setSelectedInvoices([]);
                              }
                            }}
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        {/* Status Column */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                        {/* Actions Column */}
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedAndFilteredInvoices().length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                            {showClearedInvoices
                              ? "No invoices found"
                              : "No invoices found. Mark orders as delivered to create invoices."}
                          </td>
                        </tr>
                      ) : (
                        getSortedAndFilteredInvoices().map((invoice) => {
                          const formattedDate = new Date(invoice.invoice_date).toLocaleDateString("en-AU");
                          const totalAmount = invoice.total_amount || 0;
                          const isSelected = selectedInvoices.includes(invoice.id);
                          const isCreditNote = invoice.is_credit_note;

                          return (
                            <tr key={invoice.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${isCreditNote ? 'bg-red-50' : ''}`}>
                              {/* Checkbox Cell */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedInvoices([...selectedInvoices, invoice.id]);
                                    } else {
                                      setSelectedInvoices(selectedInvoices.filter(id => id !== invoice.id));
                                    }
                                  }}
                                />
                              </td>

                              {/* Invoice Details Cells */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {invoice.invoice_number || `INV-${invoice.order_number}`}
                                  {isCreditNote && (
                                    <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                                      Credit Note
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {invoice.order_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {invoice.customer_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {invoice.customer_email}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formattedDate}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className={`text-sm font-medium ${isCreditNote ? 'text-red-600' : 'text-gray-900'}`}>
                                  {isCreditNote ? '-' : ''}${(invoice.total_amount || 0).toFixed(2)}
                                </div>
                              </td>

                              {/* Status Cell with Dropdown */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Select
                                  value={invoice.status}
                                  onValueChange={(value) => handleUpdateInvoiceStatus(invoice.id, value)}
                                  disabled={isCreditNote || invoice.is_paid}
                                >
                                  <SelectTrigger className="w-full min-w-[180px]">
                                    <SelectValue>
                                      {getStatusBadge(invoice.status)}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {/* Show only allowed transitions based on current status */}
                                    {invoice.status === INVOICE_STATUS.PENDING_REVIEW && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.APPROVED}>
                                          ✅ Approve (Move to Pending Payment)
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️ Disputed
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.APPROVED && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.PENDING_PAYMENT}>
                                          💰 Pending Payment
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️ Disputed
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.PENDING_PAYMENT && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.CLEARED}>
                                          ✔️ Cleared (Paid)
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                          ⚠️ Disputed
                                        </SelectItem>
                                      </>
                                    )}

                                    {invoice.status === INVOICE_STATUS.CLEARED && (
                                      <SelectItem value={INVOICE_STATUS.DISPUTE}>
                                        ⚠️ Reopen as Disputed
                                      </SelectItem>
                                    )}

                                    {invoice.status === INVOICE_STATUS.DISPUTE && (
                                      <>
                                        <SelectItem value={INVOICE_STATUS.APPROVED}>
                                          ✅ Re-approve after Dispute
                                        </SelectItem>
                                        <SelectItem value={INVOICE_STATUS.CLEARED}>
                                          ✔️ Cleared (Paid)
                                        </SelectItem>
                                      </>
                                    )}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-xs text-gray-500">
                                  {invoice.updated_at ?
                                    new Date(invoice.updated_at).toLocaleDateString() :
                                    new Date(invoice.created_at).toLocaleDateString()
                                  }
                                  {invoice.status === INVOICE_STATUS.PENDING_REVIEW && (
                                    <div className="text-xs text-yellow-600 font-medium mt-1">
                                      ⚠️ Updated, needs review
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Actions Cell - NO EDIT BUTTON FOR ADMIN */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex justify-center gap-2">
                                  {/* View Button */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const order = orders.find(o => o.order_number === invoice.order_number || o.id === invoice.order_id);
                                      if (order) {
                                        generateInvoice(order);
                                      } else {
                                        // If no order found, create invoice from invoice data
                                        console.log("Using invoice data instead of order");
                                        // You might want to create an alternative function here
                                        toast.info("Opening invoice view...");
                                      }
                                    }}
                                    className="flex items-center gap-1"
                                    title="View Invoice"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>

                                  {/* Approve Button */}
                                  {invoice.status === INVOICE_STATUS.PENDING_REVIEW && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUpdateInvoiceStatus(invoice.id, INVOICE_STATUS.APPROVED)}
                                      className="flex items-center gap-1 bg-green-50 text-green-700 hover:bg-green-100"
                                      title="Approve Invoice"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}

                                  {/* Clear/Pay Button */}
                                  {(invoice.status === INVOICE_STATUS.APPROVED || invoice.status === INVOICE_STATUS.PENDING_PAYMENT) && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleUpdateInvoiceStatus(invoice.id, INVOICE_STATUS.CLEARED)}
                                      className="flex items-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                      title="Mark as Paid/Cleared"
                                    >
                                      <DollarSign className="w-4 h-4" />
                                    </Button>
                                  )}

                                  {/* Dispute Button */}
                                  {invoice.status !== INVOICE_STATUS.DISPUTE && invoice.status !== INVOICE_STATUS.CLEARED && !isCreditNote && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedDisputeInvoice(invoice);
                                        setDisputeReason("");
                                        setShowDisputeDialog(true);
                                      }}
                                      className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title="Mark as Dispute"
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              {/* Last Updated Cell */}

                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Action Confirmation Dialog */}
            <Dialog open={showBulkActionDialog} onOpenChange={setShowBulkActionDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Bulk Action</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p>
                    Are you sure you want to <strong>{bulkAction.replace('_', ' ')}</strong> on {selectedInvoices.length} invoice(s)?
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setShowBulkActionDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBulkAction}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Dispute Dialog */}
            <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    Mark Invoice as Disputed
                  </DialogTitle>
                </DialogHeader>

                {selectedDisputeInvoice && (
                  <div className="space-y-4">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Invoice:</span>
                          <span className="font-medium">{selectedDisputeInvoice.invoice_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Customer:</span>
                          <span className="font-medium">{selectedDisputeInvoice.customer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-medium">${(selectedDisputeInvoice.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="dispute-reason" className="text-red-700">
                        Dispute Reason *
                      </Label>
                      <Textarea
                        id="dispute-reason"
                        value={disputeReason}
                        onChange={(e) => setDisputeReason(e.target.value)}
                        placeholder="Enter reason for dispute (e.g., wrong items, incorrect pricing, KA mismatch, etc.)"
                        rows={4}
                        className="mt-1 border-red-300 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Common reasons: Wrong item, wrong quantity, incorrect pricing, missing items, KA mismatch
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowDisputeDialog(false);
                          setSelectedDisputeInvoice(null);
                          setDisputeReason("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSubmitDispute}
                        className="bg-red-600 hover:bg-red-700"
                        disabled={!disputeReason.trim()}
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Mark as Disputed
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Credit Note Dialog */}
            <Dialog open={showCreditNoteDialog} onOpenChange={setShowCreditNoteDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Create Credit Note
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  <div>
                    <Label>Select Invoice</Label>
                    <Select
                      value={selectedInvoiceForCredit?.id || ""}
                      onValueChange={(value) => {
                        const invoice = invoices.find(inv => inv.id === value);
                        setSelectedInvoiceForCredit(invoice);
                        if (invoice) {
                          setCreditNoteForm(prev => ({
                            ...prev,
                            amount: invoice.total_amount || 0
                          }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select invoice to credit" />
                      </SelectTrigger>
                      <SelectContent>
                        {invoices
                          .filter(inv => !inv.is_credit_note && inv.status !== 'cancelled')
                          .map(invoice => (
                            <SelectItem key={invoice.id} value={invoice.id}>
                              {invoice.invoice_number} - {invoice.customer_name} - ${invoice.total_amount}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedInvoiceForCredit && (
                    <>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Customer:</span>
                            <span className="font-medium">{selectedInvoiceForCredit.customer_name}</span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-gray-600">Original Amount:</span>
                            <span className="font-medium">${selectedInvoiceForCredit.total_amount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-gray-600">Outstanding Amount:</span>
                            <span className="font-medium">
                              ${(selectedInvoiceForCredit.outstanding_amount || selectedInvoiceForCredit.total_amount).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label>Credit Reason *</Label>
                        <Select
                          value={creditNoteForm.reason}
                          onValueChange={(value) => setCreditNoteForm({ ...creditNoteForm, reason: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="returned_goods">Returned Goods</SelectItem>
                            <SelectItem value="overpayment">Overpayment</SelectItem>
                            <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                            <SelectItem value="damaged_goods">Damaged Goods</SelectItem>
                            <SelectItem value="customer_satisfaction">Customer Satisfaction</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Credit Amount *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={selectedInvoiceForCredit.outstanding_amount || selectedInvoiceForCredit.total_amount}
                          value={creditNoteForm.amount}
                          onChange={(e) => setCreditNoteForm({
                            ...creditNoteForm,
                            amount: parseFloat(e.target.value) || 0
                          })}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Maximum: ${(selectedInvoiceForCredit.outstanding_amount || selectedInvoiceForCredit.total_amount).toFixed(2)}
                        </p>
                      </div>

                      <div>
                        <Label>Notes (Optional)</Label>
                        <Textarea
                          value={creditNoteForm.notes}
                          onChange={(e) => setCreditNoteForm({ ...creditNoteForm, notes: e.target.value })}
                          placeholder="Additional notes for this credit note"
                          rows={2}
                        />
                      </div>

                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowCreditNoteDialog(false);
                            setSelectedInvoiceForCredit(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleCreateCreditNote}
                          disabled={!creditNoteForm.reason || creditNoteForm.amount <= 0}
                          className="bg-teal-500 hover:bg-teal-600"
                        >
                          Create Credit Note
                        </Button>
                      </DialogFooter>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            {/* Invoice Email Dialog - Updated */}
            <Dialog open={showInvoiceEmailDialog} onOpenChange={setShowInvoiceEmailDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Send Invoice Email
                  </DialogTitle>
                </DialogHeader>

                {selectedInvoice && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Invoice:</span>
                          <span className="font-medium">{selectedInvoice.invoice_number}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Customer:</span>
                          <span className="font-medium">{selectedInvoice.customer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">
                            {getCustomerFinanceEmail(selectedInvoice.customer_id)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Email Type</Label>
                      <Select
                        value={emailForm.emailType}
                        onValueChange={(value) => setEmailForm({ ...emailForm, emailType: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Primary Email</SelectItem>
                          <SelectItem value="operations">Operations Email</SelectItem>
                          <SelectItem value="finance">Finance/Accounts Email</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        Finance email is recommended for invoicing
                      </p>
                    </div>

                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={emailForm.subject}
                        onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={emailForm.message}
                        onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                        rows={4}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-payment"
                        checked={emailForm.includePaymentInfo}
                        onCheckedChange={(checked) => setEmailForm({
                          ...emailForm,
                          includePaymentInfo: !!checked
                        })}
                      />
                      <Label htmlFor="include-payment">Include Payment Information</Label>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowInvoiceEmailDialog(false);
                          setSelectedInvoice(null);
                        }}
                        disabled={sendingEmail}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSendInvoiceEmail}
                        disabled={sendingEmail}
                        className="bg-teal-500 hover:bg-teal-600"
                      >
                        {sendingEmail ? (
                          <>
                            <Clock className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Email
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}




        {/* Edit Request Review Dialog */}
        <Dialog
          open={showEditRequestDialog}
          onOpenChange={setShowEditRequestDialog}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Customer Edit Request</DialogTitle>
            </DialogHeader>

            {selectedEditRequest && (
              <div className="space-y-4">
                {/* Order Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Order Information
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Order Number:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.order_number}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.customer_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {selectedEditRequest.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Request Date:</span>
                      <span className="font-medium text-gray-900 ml-2">
                        {new Date(
                          selectedEditRequest.modification_requested_at
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer's Reason */}
                {selectedEditRequest.pending_modifications?.reason && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-2">
                      Customer's Reason:
                    </h3>
                    <p className="text-blue-800">
                      {selectedEditRequest.pending_modifications.reason}
                    </p>
                  </div>
                )}

                {/* Current vs Requested Changes */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h3 className="font-semibold text-red-900 mb-3">
                      Current Values
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-red-700 font-medium">Items:</span>
                        <div className="mt-1 space-y-1">
                          {selectedEditRequest.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between text-red-900 bg-red-100 px-2 py-1 rounded"
                            >
                              <span>{item.sku_name}</span>
                              <span className="font-semibold">
                                ×{item.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-red-700 font-medium">
                          Delivery Date:
                        </span>
                        <p className="text-red-900">
                          {new Date(
                            selectedEditRequest.delivery_date
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-red-700 font-medium">
                          Delivery Address:
                        </span>
                        <p className="text-red-900">
                          {selectedEditRequest.delivery_address}
                        </p>
                      </div>
                      {selectedEditRequest.special_instructions && (
                        <div>
                          <span className="text-red-700 font-medium">
                            Instructions:
                          </span>
                          <p className="text-red-900">
                            {selectedEditRequest.special_instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-green-900 mb-3">
                      Requested Changes
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-green-700 font-medium">
                          Items:
                        </span>
                        {selectedEditRequest.pending_modifications?.items ? (
                          <div className="mt-1 space-y-1">
                            {selectedEditRequest.pending_modifications.items.map(
                              (item, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between text-green-900 bg-green-100 px-2 py-1 rounded"
                                >
                                  <span>{item.sku_name}</span>
                                  <span className="font-semibold">
                                    ×{item.quantity}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="text-green-900 mt-1">No change</p>
                        )}
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Delivery Date:
                        </span>
                        <p className="text-green-900">
                          {selectedEditRequest.pending_modifications
                            ?.delivery_date
                            ? new Date(
                              selectedEditRequest.pending_modifications.delivery_date
                            ).toLocaleString()
                            : "No change"}
                        </p>
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Delivery Address:
                        </span>
                        <p className="text-green-900">
                          {selectedEditRequest.pending_modifications
                            ?.delivery_address || "No change"}
                        </p>
                      </div>
                      {selectedEditRequest.pending_modifications
                        ?.special_instructions && (
                          <div>
                            <span className="text-green-700 font-medium">
                              Instructions:
                            </span>
                            <p className="text-green-900">
                              {
                                selectedEditRequest.pending_modifications
                                  .special_instructions
                              }
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Action Selection */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">
                    Select Action:
                  </Label>
                  <div className="flex gap-4 mb-4">
                    <Button
                      variant={
                        editRequestAction === "approve" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("approve")}
                      className={
                        editRequestAction === "approve"
                          ? "bg-green-500 hover:bg-green-600"
                          : ""
                      }
                    >
                      ✓ Approve As-Is
                    </Button>
                    <Button
                      variant={
                        editRequestAction === "modify" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("modify")}
                      className={
                        editRequestAction === "modify"
                          ? "bg-blue-500 hover:bg-blue-600"
                          : ""
                      }
                    >
                      ✏️ Modify & Approve
                    </Button>
                    <Button
                      variant={
                        editRequestAction === "reject" ? "default" : "outline"
                      }
                      onClick={() => setEditRequestAction("reject")}
                      className={
                        editRequestAction === "reject"
                          ? "bg-red-500 hover:bg-red-600"
                          : ""
                      }
                    >
                      ✗ Reject
                    </Button>
                  </div>

                  {/* Modify Form */}
                  {editRequestAction === "modify" && (
                    <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                      <Label>Modify Requested Changes:</Label>
                      <div>
                        <Label className="text-sm">Delivery Date</Label>
                        <Input
                          type="datetime-local"
                          value={
                            editRequestForm.delivery_date?.slice(0, 16) || ""
                          }
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              delivery_date: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Delivery Address</Label>
                        <Input
                          value={editRequestForm.delivery_address || ""}
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              delivery_address: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Special Instructions</Label>
                        <Textarea
                          value={editRequestForm.special_instructions || ""}
                          onChange={(e) =>
                            setEditRequestForm({
                              ...editRequestForm,
                              special_instructions: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {editRequestAction === "reject" && (
                    <div className="p-4 bg-red-50 rounded-lg">
                      <Label className="text-sm">
                        Rejection Reason (Required)
                      </Label>
                      <Textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this request is being rejected..."
                        required
                      />
                    </div>
                  )}

                  {/* Admin Notes */}
                  <div className="mt-4">
                    <Label className="text-sm">Admin Notes (Optional)</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Any additional notes for the customer..."
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditRequestDialog(false)}
                    disabled={processingEditRequest}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessEditRequest}
                    disabled={processingEditRequest}
                    className={
                      editRequestAction === "approve"
                        ? "bg-green-500 hover:bg-green-600"
                        : editRequestAction === "modify"
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-red-500 hover:bg-red-600"
                    }
                  >
                    {processingEditRequest ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        {editRequestAction === "approve" && "✓ Approve Request"}
                        {editRequestAction === "modify" &&
                          "✏️ Modify & Approve"}
                        {editRequestAction === "reject" && "✗ Reject Request"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Customer Email Settings Dialog */}
        <CustomerEmailSettings
          customer={selectedCustomerForEmail}
          isOpen={showEmailSettingsDialog}
          onClose={() => {
            setShowEmailSettingsDialog(false);
            setSelectedCustomerForEmail(null);
          }}
          onSave={(updatedCustomer) => {
            // Update customer in local state if needed
            if (updatedCustomer) {
              const updatedCustomers = customers.map((c) =>
                c.id === updatedCustomer.id ? updatedCustomer : c
              );
              setCustomers(updatedCustomers);
            }
            fetchData(); // Refresh data
          }}
        />
      </div>
    </DashboardLayout>
  );
}

export default AdminDashboard;
